import { Box, Typography, Dialog, DialogContent, DialogTitle, DialogActions, Select, MenuItem, Button, TextField, CircularProgress, LinearProgress, useTheme, Avatar, alpha } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuDatabase, LuPencilLine, LuSparkles, LuCircleCheck } from "react-icons/lu";
import { ENV_FACTOR_VIEW_MODES, getVisibleFieldsForView } from "./columnDefinitions/envFactorViewModes";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import useUndo from "../../hooks/useUndo";
import { useQueryClient } from "@tanstack/react-query";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import { EnvFactor } from "../../db_access/env_factor";
import { Product } from "../../db_access/product";
import { Project } from "../../db_access/project";
import { calculateGWP } from "../../functions/CircularityFunctions";
import AddButton from "../button/AddButton";
import ColumnVisibilityButton from "../button/ColumnVisibilityButton";
import RemoveButton from "../button/RemoveButton";
import UndoButton from "../button/UndoButton";
import MaterialSearchCellEditor from "../cell-editors/MaterialSearchCellEditor";
import { TableHeaderContainer } from "../container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../container/TableTitleContainer";
import ColumnMenu from "../context-menu/ColumnMenu";
import ContextMenu from "../context-menu/ContextMenu";
import { FiCopy } from "react-icons/fi";
import LoadEnvfactorMenu from "../context-menu/LoadEnvfactorMenu";
import MaterialSearchInput from "../input/MaterialSearchInput";
import { useContextProvider } from "../page-context/ContextProvider";
import { Convert, distanceUnits, GetUnitCategory, GetUnits, volumeUnits } from "../Unit";
import AgGrid from "./AgGrid";
import UploadBOMButton from "../button/UploadBOMButton";
import ExportAsXLSXButton from "../export/ExportAsXLSXButton";
import BOMReviewDialog from "../dialog/BOMReviewDialog";
import EPDLibraryDialog from "../dialog/EPDLibraryDialog";
import arivufoot from "../../assets/logo/foot.png";
import {
  centerAlignColDef,
  defaultColDef,
  floatFormater,
  float4Formater,
  formatTo2Decimals,
  leftAlignColDef,
  nullableFloatFormater,
  rightAlignColDef,
} from "./columnDefinitions/columnDefinitions";
import GridSelectEditor from "./GridSelectEditor";
import config from "../../config";

function EnvironmentFactorGrid({
  envFactors,
  isSupplier,
  editable,
  processID,
  supplier = null,
  product = null,
  selectedProcessIds = [],
  productID = null,
  projectID = null,
  buildingID = null,
  maxHeight = undefined,
  lcaStage = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const { isSaveClicked, isSaveVisible, setIsSaveVisible, setIsSaveClicked, setIsLoading, setRefresh, columnVisibilities, envFactorOptions, enqueueAlert } = useContextProvider();
  const queryClient = useQueryClient();
  const gridHeaderContainerRef = useRef(null);
  const gridRef = useRef(null);
  const colVisibility_key = "env_factor_table_column_visibility";
  const [selectedEnvFactorIds, setSelectedEnvFactorIds] = useState([]);
  const [supplierData, setSupplierData] = useState([]);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const [envfactorMenuVisible, setEnvfactorMenuVisible] = useState(false);

  const [gridState, setGridState, undo, canUndo, resetHistory] = useUndo({
    factors: [],
    deletedIds: []
  });

  const localEnvFactors = gridState.factors;
  const envFactorIdsToBeDeleted = gridState.deletedIds;

  const setLocalEnvFactors = useCallback((nextFactorsVal, shouldRecord = true) => {
    setGridState((current) => {
      const nextFactors = typeof nextFactorsVal === "function" ? nextFactorsVal(current.factors) : nextFactorsVal;
      return {
        ...current,
        factors: nextFactors,
      };
    }, shouldRecord);
  }, [setGridState]);

  const setEnvFactorIdsToDelete = useCallback((nextDeletedVal) => {
    setGridState((current) => {
      const nextDeleted = typeof nextDeletedVal === "function" ? nextDeletedVal(current.deletedIds) : nextDeletedVal;
      return {
        ...current,
        deletedIds: nextDeleted,
      };
    }, false);
  }, [setGridState]);
  const fileInputRef = useRef(null);
  const [bomDialogOpen, setBomDialogOpen] = useState(false);
  const [parsedBomData, setParsedBomData] = useState(null);

  const [uploadStatus, setUploadStatus] = useState(null); // null | { message, step }
  const [geoDialogOpen, setGeoDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [selectedGeography, setSelectedGeography] = useState("Global Average");
  const [customGeography, setCustomGeography] = useState("");

  const [activeView, setActiveView] = useState("user_entry");
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  const [epdDialogOpen, setEpdDialogOpen] = useState(false);
  const [epdDialogMode, setEpdDialogMode] = useState("add"); // "add" | "swap"
  const [epdInitialSearch, setEpdInitialSearch] = useState("");
  const [epdTargetRowId, setEpdTargetRowId] = useState(null);

  // -- Migrator: grace period for stale local configs
  useEffect(() => {
    try {
      const MIGRATION_VERSION = "v4";
      const hasMigrated = localStorage.getItem(`${colVisibility_key}_migrated`) === MIGRATION_VERSION;
      if (hasMigrated) return;

      const raw = localStorage.getItem(colVisibility_key);
      if (raw) {
        const stored = JSON.parse(raw);
        const mandatory = ["gwp_reference", "is_hotspot", "default_unit", "mass", "source_row", "supplier_name", "user_description"];
        // Filter to ONLY items that don't already exist in legacy list
        const missing = mandatory.filter((c) => !stored.includes(c));

        if (missing.length > 0) {
          const updated = [...stored, ...missing];
          localStorage.setItem(colVisibility_key, JSON.stringify(updated));
        }
      }
      localStorage.setItem(`${colVisibility_key}_migrated`, MIGRATION_VERSION);
    } catch (err) {
      console.warn("Vis migration err:", err);
    }
  }, [colVisibility_key]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setPendingFile(file);
    setGeoDialogOpen(true);
    // Reset file input so same file can be selected again if needed
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    setGeoDialogOpen(false);
    setUploadStatus({ message: "Starting upload...", step: 1 });

    // Fire-and-forget: save BOM file to Evidence Repository independently.
    // This runs before and separate from the BOM parse — a failure on either side
    // does not affect the other.
    const evidenceIdColumn = productID ? "product_id" : projectID ? "project_id" : null;
    const evidenceEntityId = productID || projectID || null;
    if (evidenceIdColumn && evidenceEntityId) {
      const evidenceForm = new FormData();
      evidenceForm.append("files", pendingFile);
      evidenceForm.append("rowID", String(evidenceEntityId));
      evidenceForm.append("scope", "BOM");
      evidenceForm.append("IDColumnName", evidenceIdColumn);
      fetch(`${config.apiUrl}/api/upload_emission_reference_files`, { method: "POST", body: evidenceForm })
        .catch(() => { });
    }

    const formData = new FormData();
    formData.append("file", pendingFile);
    formData.append("geography", selectedGeography === "Other" ? customGeography : selectedGeography);
    formData.append("process_id", processID || "");
    formData.append("building_id", buildingID || "");
    formData.append("product_id", productID || "");

    try {
      const response = await fetch(`${config.apiUrl}/bom/upload_bom`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let finished = false;
      let finalData = null;
      let buffer = "";

      while (!finished) {
        const { value, done } = await reader.read();
        finished = done;

        if (value) {
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop(); // Keep the last (possibly incomplete) segment for next iteration

          for (const line of lines) {
            if (line.trim()) {
              try {
                const payload = JSON.parse(line);
                if (payload.status === "ping") {
                  continue;
                }
                if (payload.status === "info") {
                  setUploadStatus((prev) => {
                    const nextStep = prev ? Math.min(prev.step + 1, 6) : 2;
                    return { message: payload.message, step: nextStep };
                  });
                } else if (payload.status === "complete") {
                  finalData = payload.data;
                } else if (payload.status === "error") {
                  throw new Error(payload.message);
                }
              } catch (e) {
                if (e.message && !e.message.includes("JSON")) throw e;
              }
            }
          }
        }
      }

      // Process any remaining data left in the buffer after stream ends
      if (buffer.trim()) {
        try {
          const payload = JSON.parse(buffer);
          if (payload.status === "complete") {
            finalData = payload.data;
          } else if (payload.status === "error") {
            throw new Error(payload.message);
          }
        } catch (e) {
          if (e.message && !e.message.includes("JSON")) throw e;
        }
      }

      if (finalData && finalData.status === "ok") {
        const itemsCount = finalData.items ? finalData.items.filter((item) => item.mapping_status !== "skipped").length : 0;

        enqueueAlert({
          message: `Successfully imported ${itemsCount} materials from BOM directly into the database.`,
          severity: "success",
          duration: 10000,
        });
        setRefresh((r) => !r);
      } else if (finalData) {
        console.error("BOM Parsing Error:", finalData.message);
        enqueueAlert({ message: finalData.message || "BOM parsing failed.", severity: "error", duration: 10000 });
      }
    } catch (err) {
      console.error("Error uploading BOM:", err);
      enqueueAlert({ message: err.message || "Failed to upload BOM. Please try again.", severity: "error", duration: 10000 });
    } finally {
      setUploadStatus(null);
      setPendingFile(null);
    }
  };

  const handleBOMConfirm = (reviewedItems) => {
    setIsLoading(true);
    fetch(`${config.apiUrl}/bom/save_confirmed`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: reviewedItems,
        process_id: processID,
        building_id: buildingID,
        product_id: productID
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Save failed matching target process ID. Make sure to Save the Process to the database first.");
        return res.json();
      })
      .then((data) => {
        setBomDialogOpen(false);
        enqueueAlert({ message: `Successfully imported ${reviewedItems.length} materials from BOM.`, severity: "success", duration: 10000 });
        setRefresh((r) => !r);
      })
      .catch((err) => {
        console.error("Error saving BOM:", err);
        enqueueAlert({ message: err.message || "Failed to save BOM. Please save your workspace first.", severity: "error", duration: 10000 });
      })
      .finally(() => setIsLoading(false));
  };

  const defaultEnvFactor = useMemo(
    () => ({
      type: "",
      category: "",
      sub_category: "",
      description: "",
      default_unit: "",
      unit: "",
      factor: 1,
      unit_conversion_factor: 1,
      uncertainty: 1,
      user_factor: 1.0,
      quantity: 0,
      density: null,
      ref_unit: null,
      gwp_total: 0,
      default_gwp: 0,
      gwp_unit: 0,
      biogenic_gwp_total: 0,
      biogenic_gwp: 0,
      length_unit: null,
      length: null,
      width_unit: null,
      width: null,
      thickness_unit: null,
      thickness: null,
      part_number: null,
      note: null,
      user_description: null,
      supplier_name: null,
      source_row: null,
      process_id: processID,
      raw_material_id: null,
      _isModified: true,
    }),
    [processID]
  );
  // const treeD = useMemo(() => {
  //   let idCounter = 0;
  //   const nextId = () => idCounter++;

  //   const root = {
  //     id: nextId(),
  //     label: "Root",
  //     children: [],
  //   };

  //   // type level
  //   Object.entries(envFactorOptions || {}).forEach(([type, categoryMap]) => {
  //     const typeNode = {
  //       id: nextId(),
  //       label: type,
  //       children: [],
  //     };

  //     // category level
  //     Object.entries(categoryMap || {}).forEach(([category, subcategoryMap]) => {
  //       const categoryNode = {
  //         id: nextId(),
  //         label: category,
  //         children: [],
  //       };

  //       // subcategory level
  //       Object.entries(subcategoryMap || {}).forEach(([subcategory, descriptions]) => {
  //         const subcategoryNode = {
  //           id: nextId(),
  //           label: subcategory,
  //           children: [],
  //         };

  //         // description level (can be array or object of descriptions)
  //         if (Array.isArray(descriptions)) {
  //           descriptions.forEach((desc) => {
  //             subcategoryNode.children.push({
  //               id: nextId(),
  //               label: desc,
  //             });
  //           });
  //         } else if (descriptions && typeof descriptions === "object") {
  //           Object.keys(descriptions).forEach((descKey) => {
  //             subcategoryNode.children.push({
  //               id: nextId(),
  //               label: descKey,
  //             });
  //           });
  //         }

  //         if (subcategoryNode.children.length > 0) {
  //           categoryNode.children.push(subcategoryNode);
  //         }
  //       });

  //       if (categoryNode.children.length > 0) {
  //         typeNode.children.push(categoryNode);
  //       }
  //     });

  //     if (typeNode.children.length > 0) {
  //       root.children.push(typeNode);
  //     }
  //   });

  //   return root;
  // }, [envFactorOptions]);

  useEffect(() => {
    if (isSaveClicked) {
      const promises = [];
      const requestData = [];
      localEnvFactors.forEach((ef) => {
        if (ef._isModified) {
          const { _isModified, _selectedMaterialFromSearch, ...envFactor } = ef;
          requestData.push(envFactor);
        }
      });
      if (requestData.length > 0) {
        const savePromise = EnvFactor.save(requestData).then((res) => {
          if (!res.ok) throw new Error("Save failed");
          return res.json();
        });
        promises.push(savePromise);
      }
      if (envFactorIdsToBeDeleted.length > 0) {
        const deletePromise = EnvFactor.delete(envFactorIdsToBeDeleted).then((res) => {
          if (!res.ok) throw new Error("Delete failed");
          return res.json();
        });
        promises.push(deletePromise);
      }
      Promise.all(promises)
        .then(() => {
          // Task 1: Instead of blanking the grid, just clear _isModified flags on saved rows
          // and remove deleted rows locally — no full reload / reset needed.
          const savedIds = new Set(requestData.map((r) => r.id));
          const deletedIdSet = new Set(envFactorIdsToBeDeleted);
          setLocalEnvFactors((prev) =>
            prev
              .filter((ef) => !deletedIdSet.has(ef.id))
              .map((ef) => {
                if (savedIds.has(ef.id)) {
                  const { _isModified, _selectedMaterialFromSearch, ...clean } = ef;
                  return clean;
                }
                return ef;
              }),
            false // do NOT record this cleanup into undo history
          );
          setEnvFactorIdsToDelete([]);
          resetHistory(null); // clear undo stack without blanking the grid
        })
        .catch((err) => {
          console.error("Error during save/delete:", err);
        })
        .finally(() => {
          setIsSaveClicked(false);
          setIsLoading(false);
          setIsSaveVisible(false);

          // Invalidate cached data for the current entity to refresh charts and results
          if (productID) {
            queryClient.invalidateQueries({ queryKey: ["product", productID, "process"] });
          } else if (projectID) {
            queryClient.invalidateQueries({ queryKey: ["project", projectID, "process"] });
          } else if (buildingID) {
            queryClient.invalidateQueries({ queryKey: ["building", buildingID, "process"] });
            queryClient.invalidateQueries({ queryKey: ["building", buildingID, "results"] });
          }
        });
    }
  }, [isSaveClicked, localEnvFactors, envFactorIdsToBeDeleted, setIsLoading, setIsSaveClicked, setRefresh, productID, projectID, buildingID, queryClient, setLocalEnvFactors, setEnvFactorIdsToDelete, resetHistory]);
  useEffect(() => {
    setSelectedEnvFactorIds([]); // Clear selection when process changes to avoid stale selection IDs
    let filteredEnvFactors = [];
    if (selectedProcessIds && selectedProcessIds.length > 0) {
      filteredEnvFactors = envFactors.filter((ef) => selectedProcessIds.includes(ef.process_id));
    } else if (processID) {
      filteredEnvFactors = envFactors.filter((ef) => ef.process_id === processID);
    }

    const dbIds = new Set(filteredEnvFactors.map((ef) => ef.id));
    // Keep locally created/duplicated rows that haven't been saved yet
    const unsavedNewRows = localEnvFactors.filter((ef) => {
      const matchesProcess = selectedProcessIds && selectedProcessIds.length > 0
        ? selectedProcessIds.includes(ef.process_id)
        : ef.process_id === processID;
      return !dbIds.has(ef.id) && ef._isModified && matchesProcess;
    });

    const mappedDBRows = filteredEnvFactors.map((i) => {
      // Keep local unsaved edits if present
      const localCopy = localEnvFactors.find((le) => le.id === i.id);
      const rowToMap = (localCopy && localCopy._isModified) ? localCopy : i;

      // Fallback: if DB missing default_unit, pull it directly from live reference options
      const match = envFactorOptions?.[rowToMap.type]?.[rowToMap.category || ""]?.[rowToMap.sub_category || ""]?.[rowToMap.description]?.[0];
      let resolvedDefaultUnit = rowToMap.default_unit || match?.unit || null;
      let resolvedUnit = rowToMap.unit;
      let resolvedNote = rowToMap.note;
      let resolvedGwpReference = rowToMap.gwp_reference;
      
      if (rowToMap.type === "Raw Material Product" || rowToMap.type === "Raw Material (Supplier)") {
        const matchingSupplier = supplierData.find(
          (supplier) => (supplier.supplier ? `${supplier.supplier}_${supplier.name}` : supplier.name) === rowToMap.description
        );
        if (matchingSupplier) {
          if (resolvedUnit === "other" && matchingSupplier.other_unit) {
            resolvedUnit = matchingSupplier.other_unit;
          }
          if (resolvedDefaultUnit === "other" && matchingSupplier.other_unit) {
            resolvedDefaultUnit = matchingSupplier.other_unit;
          } else if (!resolvedDefaultUnit && matchingSupplier.functional_unit) {
            resolvedDefaultUnit = (matchingSupplier.functional_unit === "other" && matchingSupplier.other_unit) ? matchingSupplier.other_unit : matchingSupplier.functional_unit;
          }
          if (!resolvedNote) {
            resolvedNote = matchingSupplier.specifications || matchingSupplier.intro || "";
          }
          if (!resolvedGwpReference) {
            resolvedGwpReference = matchingSupplier.epd_link || "EPD Library";
          }
        }
      }

      let resolvedUncertainty = parseFloat(rowToMap.uncertainty);
      if (isNaN(resolvedUncertainty) || resolvedUncertainty === null || resolvedUncertainty === undefined) {
        resolvedUncertainty = rowToMap.type === "Transport" ? 1.03 : 1.0;
      } else if (resolvedUncertainty > 0 && resolvedUncertainty < 1) {
        resolvedUncertainty = 1.0 + resolvedUncertainty;
      } else if (resolvedUncertainty <= 0) {
        resolvedUncertainty = rowToMap.type === "Transport" ? 1.03 : 1.0;
      }

      return {
        ...rowToMap,
        unit: resolvedUnit,
        note: resolvedNote,
        gwp_reference: resolvedGwpReference,
        default_unit: resolvedDefaultUnit,
        ref_unit: rowToMap.ref_unit || match?.ref_unit || null,
        density: rowToMap.density || match?.density || null,
        density_kg_m3: rowToMap.density_kg_m3 || match?.density_kg_m3 || null,
        ref_mass_kg: rowToMap.ref_mass_kg || match?.ref_mass_kg || null,
        uncertainty: resolvedUncertainty,
        volume_unit: rowToMap.volume_unit || (rowToMap.length && rowToMap.width && rowToMap.thickness ? "m3" : undefined),
        user_length: rowToMap.user_length ?? rowToMap.length ?? null,
        user_length_unit: rowToMap.user_length_unit ?? rowToMap.length_unit ?? null,
        user_width: rowToMap.user_width ?? rowToMap.width ?? null,
        user_width_unit: rowToMap.user_width_unit ?? rowToMap.width_unit ?? null,
        user_thickness: rowToMap.user_thickness ?? rowToMap.thickness ?? null,
        user_thickness_unit: rowToMap.user_thickness_unit ?? rowToMap.thickness_unit ?? null,
        user_volume_unit: rowToMap.user_volume_unit ?? "m3",
        user_factor: rowToMap.user_factor !== undefined && rowToMap.user_factor !== null ? rowToMap.user_factor : 1.0,
      };
    });

    resetHistory({ factors: [...mappedDBRows, ...unsavedNewRows], deletedIds: [] });
  }, [envFactors, processID, envFactorOptions, selectedProcessIds, resetHistory]);
  const onCellValueChanged = async (params) => {
    const { colDef, oldValue, newValue, data } = params;
    let gwp_total = 0;
    let biogenic_gwp_total = 0;
    let newData = { ...data, [colDef.field]: newValue };

    // Task 2: Serial number renumbering — when a user changes source_row, shift all other rows
    if (colDef.field === "source_row") {
      const newSerial = parseInt(newValue, 10);
      if (!isNaN(newSerial) && newSerial > 0) {
        const editedId = data.id;
        setLocalEnvFactors((prev) => {
          // Sort current rows by their existing source_row
          const sorted = [...prev].sort((a, b) => {
            const aVal = parseInt(a.source_row, 10) || 0;
            const bVal = parseInt(b.source_row, 10) || 0;
            return aVal - bVal;
          });
          // Remove the edited row from its current position, insert at new position
          const withoutEdited = sorted.filter((r) => r.id !== editedId);
          withoutEdited.splice(newSerial - 1, 0, { ...data, source_row: newSerial, _isModified: true });
          // Re-number all rows sequentially from 1
          return withoutEdited.map((row, idx) => ({
            ...row,
            source_row: idx + 1,
            _isModified: true,
          }));
        });
        setIsSaveVisible(true);
        params.api.refreshCells({ force: true });
        return; // handled, skip standard flow
      }
    }

    // Hierarchy Reset & Auto-Population
    if (colDef.field === "type") {
      newData = { ...newData, category: "", sub_category: "", description: "", _isModified: true };
    } else if (colDef.field === "category") {
      if (newData.type === "Raw Material Product" || newData.type === "Raw Material (Supplier)") {
        newData = populateEnvFactor({ ...newData, _isModified: true });
      } else {
        newData = { ...newData, sub_category: "", description: "", _isModified: true };
      }
    } else if (colDef.field === "sub_category") {
      newData = { ...newData, description: "", _isModified: true };
    } else if (colDef.field === "description") {
      const { type, category, sub_category } = newData;

      // Raw Material Product type: ALWAYS populate from supplier library
      if (type === "Raw Material Product" || type === "Raw Material (Supplier)") {
        newData = populateEnvFactor({ ...newData, description: newValue });

      } else {
        // For normal DB materials: check if the selected value is a real DB option
        const validDBOptions =
          type && category && envFactorOptions?.[type]?.[category || ""]?.[sub_category || ""]
            ? Object.keys(envFactorOptions[type][category || ""][sub_category || ""])
            : [];

        if (validDBOptions.includes(newValue)) {
          // User selected a DB material → populate GWP, unit, density etc. from library
          newData = populateEnvFactor({ ...newData, description: newValue });
        } else {
          // User kept/selected their own BOM name → preserve all existing data, just update label
          newData = { ...newData, description: newValue, _isModified: true };
        }
      }
    } else if (colDef.field === "unit") {
      const refUnit = newData.default_unit || oldValue || newValue;
      const default_gwp = (parseFloat(newData.default_gwp) !== 0 && !isNaN(parseFloat(newData.default_gwp)))
        ? parseFloat(newData.default_gwp)
        : (parseFloat(newData.gwp_unit) || 0);
      const default_bio = parseFloat(newData.default_biogenic_gwp) || 0;
      const isStageD = newData.lca_stage && newData.lca_stage.trim().startsWith("D");
      const isBioLegacy = newData.description?.includes("(Biogenic)") || (default_gwp < 0 && !isStageD);

      if (isBioLegacy) {
        newData = {
          ...newData,
          gwp_unit: 0,
          biogenic_gwp: Convert(default_gwp, newValue, refUnit),
          _isModified: true,
        };
      } else {
        newData = {
          ...newData,
          gwp_unit: Convert(default_gwp, newValue, refUnit),
          biogenic_gwp: default_bio ? Convert(default_bio, newValue, refUnit) : 0,
          _isModified: true,
        };
      }
    } else if (colDef.field === "length_unit") {
      const fromUnit = oldValue || newData.length_unit || "m";
      newData = { ...newData, length: Convert(newData.length, fromUnit, newValue) };
    } else if (colDef.field === "width_unit") {
      const fromUnit = oldValue || newData.width_unit || "m";
      newData = { ...newData, width: Convert(newData.width, fromUnit, newValue) };
    } else if (colDef.field === "thickness_unit") {
      const fromUnit = oldValue || newData.thickness_unit || "mm";
      newData = { ...newData, thickness: Convert(newData.thickness, fromUnit, newValue) };
    } else if (colDef.field === "user_length_unit") {
      const base = newData.user_length ?? newData.length;
      const fromUnit = oldValue || newData.length_unit || "m";
      newData = { ...newData, user_length: Convert(base, fromUnit, newValue) };
    } else if (colDef.field === "user_width_unit") {
      const base = newData.user_width ?? newData.width;
      const fromUnit = oldValue || newData.width_unit || "m";
      newData = { ...newData, user_width: Convert(base, fromUnit, newValue) };
    } else if (colDef.field === "user_thickness_unit") {
      const base = newData.user_thickness ?? newData.thickness;
      const fromUnit = oldValue || newData.thickness_unit || "mm";
      newData = { ...newData, user_thickness: Convert(base, fromUnit, newValue) };
    } else if (colDef.field === "gwp_unit") {
      newData = { ...newData, default_gwp: newValue, _isModified: true };
    } else if (colDef.field === "biogenic_gwp") {
      newData = { ...newData, default_biogenic_gwp: newValue, _isModified: true };
    }

    if (newData.type === "User Entry") {
      newData._isModified = true;
    } else {

    // Dimension scaling on user_* fields — reference fields stay untouched
    if (["user_length", "user_width", "user_thickness"].includes(colDef.field)) {
      if (!newValue || newValue <= 0) {
        console.warn("Invalid user dimension — reverting", colDef.field);
        newData = { ...newData, [colDef.field]: oldValue };
      } else {
        let updatedFactor = newData.factor || 1.0;
        const default_unit_raw = (newData.default_unit || "").trim().toLowerCase();

        if (oldValue > 0) {
          // Existing dimension changed — scale factor by the ratio (works for all material types)
          const ratio = newValue / oldValue;
          updatedFactor = updatedFactor * ratio;
        } else if (default_unit_raw === "m3") {
          // Fix: First-time dimension entry on a volumetric (m3) reference material.
          // oldValue is null so ratio is impossible. Instead compute factor = physical volume
          // so GWP = qty × gwp_per_m3 × volume × uncertainty (formula stays unchanged).
          const lVal = colDef.field === "user_length" ? newValue : (newData.user_length ?? newData.length);
          const wVal = colDef.field === "user_width" ? newValue : (newData.user_width ?? newData.width);
          const tVal = colDef.field === "user_thickness" ? newValue : (newData.user_thickness ?? newData.thickness);
          const lUnit = newData.user_length_unit ?? newData.length_unit ?? "m";
          const wUnit = newData.user_width_unit ?? newData.width_unit ?? "m";
          const tUnit = newData.user_thickness_unit ?? newData.thickness_unit ?? "m";
          if (lVal && wVal && tVal) {
            const lm = Convert(parseFloat(lVal), lUnit, "m");
            const wm = Convert(parseFloat(wVal), wUnit, "m");
            const tm = Convert(parseFloat(tVal), tUnit, "m");
            if (lm && wm && tm) {
              updatedFactor = lm * wm * tm; // factor = volume in m³
            }
          }
          // If not all 3 dims entered yet — factor stays 1.0 (correct, partial entry)
        }

        // Force unit to "unit" irrespective of anything
        const targetUnit = "unit";
        const default_gwp = parseFloat(newData.default_gwp) || 0;
        const default_bio = parseFloat(newData.default_biogenic_gwp) || 0;
        const isStageD = newData.lca_stage && newData.lca_stage.trim().startsWith("D");
        const isBioLegacy = newData.description?.includes("(Biogenic)") || (default_gwp < 0 && !isStageD);

        if (isBioLegacy) {
          newData = {
            ...newData,
            factor: updatedFactor,
            unit: targetUnit,
            gwp_unit: 0,
            biogenic_gwp: Convert(default_gwp, targetUnit, newData.default_unit),
            _isModified: true
          };
        } else {
          newData = {
            ...newData,
            factor: updatedFactor,
            unit: targetUnit,
            gwp_unit: Convert(default_gwp, targetUnit, newData.default_unit),
            biogenic_gwp: default_bio ? Convert(default_bio, targetUnit, newData.default_unit) : 0,
            _isModified: true
          };
        }
      }
    }
    // Guard old reference fields — block zero/negative but do NOT scale them
    if (["length", "width", "thickness"].includes(colDef.field)) {
      if (!newValue || newValue <= 0) {
        newData = { ...newData, [colDef.field]: oldValue };
      }
    }

    // Recalculate totals if inputs are present
    if (newData.quantity !== undefined) {
      const q = parseFloat(newData.quantity) || 0;
      const f = parseFloat(newData.factor) !== undefined ? parseFloat(newData.factor) : 1.0;
      const ucf = parseFloat(newData.unit_conversion_factor) !== undefined ? parseFloat(newData.unit_conversion_factor) : 1.0;
      const uf = parseFloat(newData.user_factor) !== undefined && parseFloat(newData.user_factor) !== null ? parseFloat(newData.user_factor) : 1.0;

      let u = parseFloat(newData.uncertainty);
      if (isNaN(u) || u === null || u === undefined) {
        u = newData.type === "Transport" ? 1.03 : 1.0;
      } else if (u > 0 && u < 1) {
        u = 1.0 + u;
      } else if (u <= 0) {
        u = newData.type === "Transport" ? 1.03 : 1.0;
      }
      newData.uncertainty = u;

      const gwp_u = parseFloat(newData.gwp_unit) || 0;
      newData.gwp_total = q * f * ucf * gwp_u * u * uf;

      const bio_gwp_u = parseFloat(newData.biogenic_gwp) || 0;
      newData.biogenic_gwp_total = q * f * ucf * bio_gwp_u * u * uf;

      newData.mass = calculatePhysicalMass(newData);

      newData._isModified = true;
    }
    }

    setLocalEnvFactors((prev) =>
      prev.map((ef) => {
        if (ef.id === data.id) {
          return { ...ef, ...newData };
        } else {
          return ef;
        }
      })
    );
    if (newData._isModified) {
      setIsSaveVisible(true);
    }
    params.api.refreshCells({ rowNodes: [params.node], force: true });
  };
  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedEnvFactorIds(selected.map((item) => item.id));
  };
  const selectedEnvFactorsLocal = useMemo(() => {
    return localEnvFactors.filter((ef) => selectedEnvFactorIds.includes(ef.id));
  }, [localEnvFactors, selectedEnvFactorIds]);

  const duplicateEnvFactor = (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) return;
    setLocalEnvFactors((prev) => {
      const duplicated = selectedItems.map((item) => {
        return {
          ...item,
          id: uuidv4(),
          description: item.description ? `${item.description} - Copy` : "Copy",
          _isModified: true
        };
      });
      return [...prev, ...duplicated];
    });
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Row duplicated successfully"), severity: "success" });
  };

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    const container = gridRef.current?.eGridDiv || gridHeaderContainerRef.current?.closest('.MuiPaper-root');
    const xPos = container ? container.getBoundingClientRect().left : e.clientX;
    setMenuPosition({ x: xPos, y: e.clientY });
    setMenuVisible(true);
  }, []);

  const handleCloseMenu = () => {
    setMenuVisible(false);
  };

  const contextMenuOptions = [
    { label: t("Duplicate"), action: duplicateEnvFactor, icon: <FiCopy />, needSelectedItems: true },
    {
      label: "Swap via Library",
      action: (selected) => {
        const item = selected?.[0];
        setEpdDialogMode("swap");
        setEpdTargetRowId(item?.id || selectedEnvFactorIds[0] || null);
        setEpdInitialSearch(item?.description || item?.name || "");
        setEpdDialogOpen(true);
      },
      icon: <LuDatabase />,
      needSelectedItems: true,
    },
  ];
  const getVolume = useCallback((data) => {
    if (!data.length || !data.width || !data.thickness) return 0;
    if (!data.length_unit || !data.width_unit || !data.thickness_unit) return 0;
    if (!data.volume_unit) return 0;
    const lengthInM = Convert(data.length, data.length_unit, "m");
    const widthInM = Convert(data.width, data.width_unit, "m");
    const thicknessInM = Convert(data.thickness, data.thickness_unit, "m");
    // console.log("Length in m:", lengthInM, "Width in m:", widthInM, "Thickness in m:", thicknessInM);
    const volumeInM3 = lengthInM * widthInM * thicknessInM;
    return volumeInM3;
  }, []);
  // getUserVolume reads user_* dimension fields (falls back to reference if unset)
  const getUserVolume = useCallback((data) => {
    const l = data.user_length ?? data.length;
    const lu = data.user_length_unit ?? data.length_unit;
    const w = data.user_width ?? data.width;
    const wu = data.user_width_unit ?? data.width_unit;
    const t = data.user_thickness ?? data.thickness;
    const tu = data.user_thickness_unit ?? data.thickness_unit;
    if (!l || !w || !t || !lu || !wu || !tu) return 0;
    const lm = Convert(l, lu, "m");
    const wm = Convert(w, wu, "m");
    const tm = Convert(t, tu, "m");
    return lm * wm * tm;
  }, []);

  const calculatePhysicalMass = useCallback((newData) => {
    const qty = parseFloat(newData.quantity);
    if (isNaN(qty) || qty === null) return null;
    if (qty === 0.0) return 0.0;

    const unit = (newData.unit || "").trim().toLowerCase();
    const ref_unit = (newData.ref_unit || "").trim().toLowerCase();
    const factor = parseFloat(newData.factor) !== undefined ? parseFloat(newData.factor) : 1.0;
    const unit_conversion_factor = parseFloat(newData.unit_conversion_factor) !== undefined ? parseFloat(newData.unit_conversion_factor) : 1.0;

    const weightUnits = ["mg", "g", "kg", "ton", "oz", "lb", "t", "tonne", "tonnes", "kilogram", "kilograms", "gram", "grams", "pound", "pounds", "us ton (907.185 kg)"];
    const default_unit = (newData.default_unit || "").trim().toLowerCase();

    const l = newData.user_length ?? newData.length;
    const lu = newData.user_length_unit ?? newData.length_unit;
    const w = newData.user_width ?? newData.width;
    const wu = newData.user_width_unit ?? newData.width_unit;
    const t = newData.user_thickness ?? newData.thickness;
    const tu = newData.user_thickness_unit ?? newData.thickness_unit;

    const lm = (l && lu) ? Convert(l, lu, "m") : null;
    const wm = (w && wu) ? Convert(w, wu, "m") : null;
    const tm = (t && tu) ? Convert(t, tu, "m") : null;

    const density_kg_m3 = parseFloat(newData.density_kg_m3) || parseFloat(newData.density) || null;
    const ref_mass_kg = parseFloat(newData.ref_mass_kg) || null;

    // Prioritize physical dimensions/geometry calculations ONLY if the row's unit is not a raw weight unit.
    // This allows custom-dimensioned materials (whose default_unit is 'kg') to calculate mass physically.
    if (!weightUnits.includes(unit)) {
      const piece_units = ["unit", "piece", "pcs", "pc", "each", "item", "items"];
      if (piece_units.includes(unit)) {
        if (lm && wm && tm && density_kg_m3) {
          const vol_m3 = lm * wm * tm;
          return Math.abs(qty * vol_m3 * density_kg_m3);
        }
      }

      if (GetUnitCategory(unit) === "area") {
        const area_m2 = Convert(qty, unit, "m2");
        if (area_m2 !== null) {
          if (tm && density_kg_m3) {
            return Math.abs(area_m2 * tm * density_kg_m3);
          }
          if (ref_unit === "kg/m2" && density_kg_m3) {
            return Math.abs(area_m2 * density_kg_m3);
          }
        }
      }

      if (GetUnitCategory(unit) === "distance") {
        const length_m = Convert(qty, unit, "m");
        if (length_m !== null) {
          if (wm && tm && density_kg_m3) {
            return Math.abs(length_m * wm * tm * density_kg_m3);
          }
          if (ref_unit === "kg/m" && density_kg_m3) {
            return Math.abs(length_m * density_kg_m3);
          }
          if (ref_unit === "kg/m2" && density_kg_m3) {
            if (wm) return Math.abs(length_m * wm * density_kg_m3);
            return Math.abs(length_m * density_kg_m3);
          }
        }
      }

      if (GetUnitCategory(unit) === "volume") {
        const vol_m3 = Convert(qty, unit, "m3");
        if (vol_m3 !== null && density_kg_m3) {
          return Math.abs(vol_m3 * density_kg_m3);
        }
      }
    }

    // Fallback B: Direct user unit is weight-based
    if (weightUnits.includes(unit)) {
      return Math.abs(Convert(qty * factor, unit, "kg") || 0.0);
    }

    // Fallback A: Default EPD weight-based scaling
    if (weightUnits.includes(default_unit)) {
      const base_mass = qty * factor * unit_conversion_factor;
      if (default_unit === "kg" || default_unit === "kilogram" || default_unit === "kilograms") {
        return Math.abs(base_mass);
      }
      return Math.abs(Convert(base_mass, default_unit, "kg") || 0.0);
    }

    // Fallback C: reference mass based scaling
    if (ref_mass_kg) {
      return Math.abs(qty * unit_conversion_factor * ref_mass_kg * factor);
    }
    return null;
  }, []);

  const onLoadEnvFactors = (loadedEnvFactors) => {
    setLocalEnvFactors((p) => {
      let maxSourceRow = p.reduce((max, row) => {
        const val = parseInt(row.source_row, 10);
        return !isNaN(val) && val > max ? val : max;
      }, 0);

      const mapped = loadedEnvFactors.map((ef) => {
        maxSourceRow += 1;
        return populateEnvFactor({
          ...ef,
          id: uuidv4(),
          process_id: processID,
          source_row: maxSourceRow,
          _isModified: true
        });
      });

      return [...p, ...mapped];
    });
    setIsSaveVisible(true);
  };

  const populateEnvFactor = (ef) => {
    const ret = { ...ef };
    if (!ret.type) ret.type = ef.source_type === "north_american_epd" ? "North American EPD" : "Raw Material";
    if (!ret.category) ret.category = "General";
    if (ret.type && ret.category && ret.description) {
      let firstMatch = null;
      if (ret.type === "Raw Material Product" || ret.type === "Raw Material (Supplier)") {
        const matchingSupplier = supplierData.find(
          (supplier) => (supplier.supplier ? `${supplier.supplier}_${supplier.name}` : supplier.name) === ret.description
        );
        if (matchingSupplier) {
          const isReused = ret.category === "Reused Product";
          const gwpVal = isReused
            ? (matchingSupplier.reused_gwp !== undefined && matchingSupplier.reused_gwp !== null ? matchingSupplier.reused_gwp : (matchingSupplier.reuse_gwp !== undefined ? matchingSupplier.reuse_gwp : 0))
            : (matchingSupplier.gwp !== undefined ? matchingSupplier.gwp : 0);

          firstMatch = {
            unit: (matchingSupplier.functional_unit === "other" && matchingSupplier.other_unit) ? matchingSupplier.other_unit : matchingSupplier.functional_unit,
            gwp_per_unit: gwpVal,
            biogenic_gwp_per_unit: matchingSupplier.biogenic_gwp !== undefined ? matchingSupplier.biogenic_gwp : 0,
            length: matchingSupplier.length || null,
            length_unit: matchingSupplier.length_unit || null,
            width: matchingSupplier.width || null,
            width_unit: matchingSupplier.width_unit || null,
            thickness: matchingSupplier.thickness || null,
            thickness_unit: matchingSupplier.thickness_unit || null,
            ref_mass_kg: (matchingSupplier.total_mass !== undefined && matchingSupplier.total_mass !== null) ? matchingSupplier.total_mass : null,
            note: matchingSupplier.specifications || matchingSupplier.intro || "",
            gwp_reference: matchingSupplier.epd_link || "EPD Library",
          };
        }
      } else {
        const matchedEnvFactors = envFactorOptions?.[ret.type]?.[ret.category || ""]?.[ret.sub_category || ""]?.[ret.description] || [];
        firstMatch = matchedEnvFactors[0];
      }
      if (!firstMatch) {
        firstMatch = {};
      }

      let unit = ef?.unit || firstMatch?.unit || null;
      if ((ret.type === "Raw Material Product" || ret.type === "Raw Material (Supplier)") && firstMatch?.unit) {
        unit = firstMatch.unit;
      }
      const gwp_per_unit = ef?.gwp_per_unit !== undefined ? ef.gwp_per_unit : (firstMatch?.gwp_per_unit || 0);
      const biogenic_gwp_per_unit = ef?.biogenic_gwp_per_unit !== undefined ? ef.biogenic_gwp_per_unit : (firstMatch?.biogenic_gwp_per_unit || 0);

      ret["default_unit"] = unit;
      ret["unit"] = unit;

      ret["default_gwp"] = gwp_per_unit;
      ret["default_biogenic_gwp"] = biogenic_gwp_per_unit;

      const isStageD = ret.lca_stage && ret.lca_stage.trim().startsWith("D");
      const isBioLegacy = ret.description.includes("(Biogenic)") || (gwp_per_unit < 0 && !isStageD);

      if (isBioLegacy) {
        ret["gwp_unit"] = 0;
        ret["biogenic_gwp"] = gwp_per_unit;
      } else {
        ret["gwp_unit"] = gwp_per_unit;
        ret["biogenic_gwp"] = biogenic_gwp_per_unit;
      }

      ret["unit_conversion_factor"] = ef?.unit_conversion_factor !== undefined ? ef.unit_conversion_factor : 1.0;
      ret["quantity"] = ef?.quantity !== undefined && ef?.quantity !== null ? ef.quantity : 1;
      ret["factor"] = ef?.factor !== undefined && ef?.factor !== null ? ef.factor : 1.0;
      ret["user_factor"] = ef?.user_factor !== undefined && ef?.user_factor !== null ? ef.user_factor : 1.0;

      ret["density_kg_m3"] = firstMatch?.density_kg_m3 !== undefined ? firstMatch.density_kg_m3 : (ef?.density_kg_m3 || null);
      ret["gwp_per_m3"] = firstMatch?.gwp_per_m3 !== undefined ? firstMatch.gwp_per_m3 : (ef?.gwp_per_m3 || null);
      ret["ref_mass_kg"] = firstMatch?.ref_mass_kg !== undefined ? firstMatch.ref_mass_kg : (ef?.ref_mass_kg || null);
      ret["uncertainty_factor"] = firstMatch?.uncertainty_factor !== undefined ? firstMatch.uncertainty_factor : (ef?.uncertainty_factor || null);

      // Apply Wood biogenic formula if type is Wood and biogenic_gwp is 0/empty
      if (ret.type === "Wood" && (!ret["biogenic_gwp"] || parseFloat(ret["biogenic_gwp"]) === 0)) {
        const refMass = parseFloat(ret.ref_mass_kg || 0);
        if (refMass > 0) {
          ret["biogenic_gwp"] = -1.0 * refMass * 0.5 * (44.0 / 12.0);
          ret["default_biogenic_gwp"] = ret["biogenic_gwp"];
        }
      }

      // Fix: Always prefer the reference DB uncertainty_factor when populating a material.
      // Saved rows carry stale uncertainty=1.0 (baked at first save). The reference DB
      // has the correct value (e.g. 0.03 which normalises to 1.03).
      const rawUncertainty = firstMatch?.uncertainty_factor != null
        ? firstMatch.uncertainty_factor          // prefer live reference value
        : ef?.uncertainty;                       // fall back to saved row only if no reference
      let uVal = parseFloat(rawUncertainty);
      if (isNaN(uVal) || uVal === null || uVal === undefined) {
        uVal = ret.type === "Transport" ? 1.03 : 1.0;
      } else if (uVal > 0 && uVal < 1) {
        uVal = 1.0 + uVal;  // e.g. 0.03 → 1.03
      } else if (uVal <= 0) {
        uVal = ret.type === "Transport" ? 1.03 : 1.0;
      }
      ret["uncertainty"] = uVal;

      ret["gwp_total"] = calculateGWP(ret.quantity, ret.gwp_unit, ret.factor, ret.uncertainty, ret.unit_conversion_factor, ret.type, ret.user_factor);
      ret["biogenic_gwp_total"] = calculateGWP(ret.quantity, ret.biogenic_gwp, ret.factor, ret.uncertainty, ret.unit_conversion_factor, ret.type, ret.user_factor);

      ret["density"] = firstMatch?.density !== undefined ? firstMatch.density : (ef?.density || null);
      ret["ref_unit"] = firstMatch?.ref_unit !== undefined ? firstMatch.ref_unit : (ef?.ref_unit || null);
      ret["length"] = firstMatch?.length !== undefined ? firstMatch.length : (ef?.length || null);
      ret["length_unit"] = firstMatch?.length_unit !== undefined ? firstMatch.length_unit : (ef?.length_unit || null);
      ret["width"] = firstMatch?.width !== undefined ? firstMatch.width : (ef?.width || null);
      ret["width_unit"] = firstMatch?.width_unit !== undefined ? firstMatch.width_unit : (ef?.width_unit || null);
      ret["thickness"] = firstMatch?.thickness !== undefined ? firstMatch.thickness : (ef?.thickness || null);
      ret["thickness_unit"] = firstMatch?.thickness_unit !== undefined ? firstMatch.thickness_unit : (ef?.thickness_unit || null);
      ret["volume"] = firstMatch?.volume !== undefined ? firstMatch.volume : (ef?.volume || getVolume(ret));
      ret["volume_unit"] = firstMatch?.volume_unit !== undefined ? firstMatch.volume_unit : (ef?.volume_unit || (ret.length && ret.width && ret.thickness ? "m3" : undefined));
      ret["year"] = firstMatch?.year !== undefined ? firstMatch.year : (ef?.year || null);
      ret["gwp_reference"] = firstMatch?.gwp_reference !== undefined ? firstMatch.gwp_reference : (ef?.gwp_reference || null);
      ret["geography"] = firstMatch?.geography !== undefined ? firstMatch.geography : (ef?.geography || null);
      ret["lcia_method"] = firstMatch?.lcia_method !== undefined ? firstMatch.lcia_method : (ef?.lcia_method || null);
      ret["recyclability"] = firstMatch?.recyclability !== undefined ? firstMatch.recyclability : (ef?.recyclability || null);
      ret["note"] = firstMatch?.note !== undefined ? firstMatch.note : (ef?.note || null);
      ret["supplier_name"] = ef?.manufacturer || ef?.supplier_name || firstMatch?.supplier_name || ret["supplier_name"] || null;

      // Pre-fill user_* from reference (user hasn't overridden yet)
      ret["user_length"] = ret.length;
      ret["user_length_unit"] = ret.length_unit;
      ret["user_width"] = ret.width;
      ret["user_width_unit"] = ret.width_unit;
      ret["user_thickness"] = ret.thickness;
      ret["user_thickness_unit"] = ret.thickness_unit;
      ret["user_volume_unit"] = "m3";
      ret["mass"] = calculatePhysicalMass(ret);
    }
    return ret;
  };

  const onSearchSelect = (material) => {
    if (selectedEnvFactorIds.length > 0) {
      setLocalEnvFactors((prev) =>
        prev.map((ef) => {
          if (selectedEnvFactorIds.includes(ef.id)) {
            return populateEnvFactor({
              ...ef,
              ...material, // Pass all material properties including gwp_per_unit and unit
              type: material.type,
              category: material.category,
              sub_category: material.sub_category,
              description: material.description,
              _isModified: true,
            });
          }
          return ef;
        })
      );
      setIsSaveVisible(true);
    } else {
      onLoadEnvFactors([material]);
    }
  };

  const handleEPDSelect = (selectedInput) => {
    const materials = Array.isArray(selectedInput) ? selectedInput : [selectedInput];
    if (epdDialogMode === "swap" && (epdTargetRowId || selectedEnvFactorIds.length > 0)) {
      const targetId = epdTargetRowId || selectedEnvFactorIds[0];
      const selectedMaterial = materials[0];
      if (selectedMaterial) {
        setLocalEnvFactors((prev) =>
          prev.map((ef) => {
            if (ef.id === targetId || selectedEnvFactorIds.includes(ef.id)) {
              return populateEnvFactor({
                ...ef,
                ...selectedMaterial,
                type: selectedMaterial.type || "North American EPD",
                category: selectedMaterial.category || "General",
                sub_category: selectedMaterial.sub_category || "",
                description: selectedMaterial.description,
                _isModified: true,
              });
            }
            return ef;
          })
        );
        setEpdTargetRowId(null);
        setIsSaveVisible(true);
        enqueueAlert({
          message: `Material swapped with ${selectedMaterial.display_name || selectedMaterial.description}.`,
          severity: "success",
        });
      }
    } else {
      onLoadEnvFactors(materials);
      enqueueAlert({
        message:
          materials.length === 1
            ? `Added ${materials[0].display_name || materials[0].description} from Resource & EPD Library.`
            : `Added ${materials.length} materials from Resource & EPD Library.`,
        severity: "success",
      });
    }
  };

  const onUploadEnvFactors = (uploadedEnvFactors) => {
    console.log("Uploaded Env Factors:", uploadedEnvFactors);
    EnvFactor.ai(uploadedEnvFactors)
      .then((response) => response.json())
      .then((data) => {
        console.log("AI Response:", data);
        // Handle the AI response here
      })
      .catch((error) => console.error("Error with AI request:", error));
  };
  useEffect(() => {
    Product.get_mine()
      .then((response) => response.json())
      .then((data) => setSupplierData(data || []))
      .catch((error) => console.error("Error fetching data:", error));
  }, []);
  const [projectData, setProjectData] = useState([]);
  useEffect(() => {
    Project.get_from_my_workspace()
      .then((response) => response.json())
      .then((data) => {
        setProjectData(data || []);
      })
      .catch((error) => console.error("Error fetching projects:", error));
  }, []);

  const computedHeight = useMemo(() => {
    const rowCount = localEnvFactors.length;
    const effectiveRows = Math.max(5, rowCount); // Default to at least 5 rows space
    const rowHeight = 48;
    const headerHeight = 48;
    const footerHeight = 72; // AgGrid pagination is true
    const scrollbarHeight = 18;
    const total = (effectiveRows * rowHeight) + headerHeight + footerHeight + scrollbarHeight + 4;
    return maxHeight ? `min(${total}px, ${maxHeight})` : `${total}px`;
  }, [localEnvFactors.length, maxHeight]);

  const exportFilename = useMemo(() => {
    if (product?.name) {
      return `${product.name.toLowerCase().replace(/\s+/g, "_")}_envfactors.xlsx`;
    }
    const activeProject = projectData.find((p) => String(p.id) === String(projectID));
    if (activeProject?.name) {
      return `${activeProject.name.toLowerCase().replace(/\s+/g, "_")}_envfactors.xlsx`;
    }
    return "environmental_factors.xlsx";
  }, [product, projectID, projectData]);

  const colDefs = useMemo(() => {
    const isHidden = (field) => {
      // Hide transport-irrelevant columns if A2 (transportation) is selected
      const isTransportField = [
        "mass", "biogenic_gwp", "biogenic_gwp_total",
        "length", "length_unit", "width", "width_unit", "thickness", "thickness_unit",
        "user_length", "user_length_unit", "user_width", "user_width_unit", "user_thickness", "user_thickness_unit",
        "part_number"
      ].includes(field);

      const isTransportation = lcaStage && (lcaStage.startsWith("A2") || lcaStage.toLowerCase().includes("transport"));
      if (isTransportation && isTransportField) {
        return true;
      }

      // In specific modes, bypass stored custom visibility and force relevant columns ONLY!
      const visibleFields = getVisibleFieldsForView(activeView);
      if (visibleFields !== null && !visibleFields.includes(field)) {
        return true;
      }

      // Full grid view defaults back to custom persistence stored preferences
      const stored = columnVisibilities[colVisibility_key];
      if (!stored) {
        // By default, hide type, category, and sub_category in user entry view
        if (["type", "category", "sub_category"].includes(field)) {
          return true;
        }
        return false;
      }

      // Automatically tie unit columns visibility to their parent fields
      const unitToParent = {
        "length_unit": "length",
        "width_unit": "width",
        "thickness_unit": "thickness",
        "user_length_unit": "user_length",
        "user_width_unit": "user_width",
        "user_thickness_unit": "user_thickness",
        "user_volume_unit": "user_volume"
      };

      const parent = unitToParent[field];
      if (parent) {
        return !stored.includes(parent);
      }

      return !stored.includes(field);
    };
    const isUnitEditable = (params) => {
      if (!editable) return false;
      // In user entry mode, allow editing dimensions for all unit types
      if (activeView === "user_entry") return true;

      const { data } = params;
      const unitCategory = GetUnitCategory(data.unit);

      // Strict rule for other modes: if it's a piece or unit, dimensions are NEVER editable.
      if (unitCategory === "piece" || unitCategory === "item" || (data.unit || "").toLowerCase() === "unit") {
        return false;
      }

      return ["volume", "area", "distance"].includes(unitCategory);
    };

    const isThicknessEditable = (params) => {
      // Inherit the piece/unit restriction from isUnitEditable
      return isUnitEditable(params);
    };

    const makeEditorParams = (vals) => (params) => {
      const onValueChanged = (value) => {
        const newParams = { ...params, data: { ...params.data, [params.colDef.field]: value }, newValue: value, oldValue: params.value };
        onCellValueChanged(newParams);
      };
      return { values: vals, onCellValueChanged: onValueChanged };
    };

    // Reference cell style: header-row background + gray text to signal "read only"
    const getRefStyle = (baseStyle) => (params) => ({
      ...baseStyle,
      backgroundColor: isDark ? "#0f172a" : "#f1f5f9",
      color: isDark ? "#64748b" : "#64748b", // Subtle but readable gray
      cursor: "default",
    });
    // Derived cell style: subtle hint for computed columns (no italics)
    const getDerivedStyle = (baseStyle) => (params) => ({
      ...baseStyle,
      backgroundColor: isDark ? "#111827" : "#f8fafc",
      color: isDark ? "#475569" : "#475569", // Darker gray for derived
      cursor: "default",
    });

    const getVolumeInM3 = (params) => getVolume(params.data);
    const getVolumeInTargetUnit = (params) => {
      const vm3 = getVolumeInM3(params);
      return vm3 ? Convert(vm3, "m3", params.data.volume_unit || "m3") : null;
    };
    const getDensity_kg_per_m3 = (params) => {
      const vm3 = getVolumeInM3(params);
      const { data } = params;
      if (!data.density || !vm3) return null;
      return data.density / vm3;
    };
    const getUserVolumeInTargetUnit = (params) => {
      const vm3 = getUserVolume(params.data);
      const targetUnit = params.data.user_volume_unit || "m3";
      return vm3 ? Convert(vm3, "m3", targetUnit) : null;
    };

    const baseCols = [
      // ════ SERIAL NUMBER ════
      {
        headerName: "Sl No.",
        field: "source_row",
        ...defaultColDef,
        ...rightAlignColDef,
        editable: editable,
        cellEditor: "agTextCellEditor",
        pinned: activeView === "reference" ? "left" : null,
        width: 100,
        minWidth: 80,
        maxWidth: 120,
        cellStyle: (params) => {
          const base = rightAlignColDef.cellStyle ? (typeof rightAlignColDef.cellStyle === "function" ? rightAlignColDef.cellStyle(params) : rightAlignColDef.cellStyle) : {};
          if (!editable) {
            return {
              ...base,
              backgroundColor: isDark ? "#0f172a" : "#f1f5f9",
              color: isDark ? "#64748b" : "#64748b",
              cursor: "default",
            };
          }
          return base;
        },
        valueFormatter: (params) => (params.value != null ? params.value : ""),
        _group_id: "user",
      },
      // ════ LEAD HIERARCHY (User Entry focus) ════
      {
        headerName: "Type", field: "type", ...defaultColDef, ...leftAlignColDef, editable: editable, _group_id: "user", pinned: null,
        cellEditorSelector: (params) => {
          const types = ["User Entry", "Raw Material Product", ...Object.keys(envFactorOptions || {})];
          return { component: GridSelectEditor, params: makeEditorParams(types)(params) };
        }
      },
      {
        headerName: "Category", field: "category", ...defaultColDef, ...leftAlignColDef, editable: editable, _group_id: "user", pinned: null,
        cellEditorSelector: (params) => {
          const type = params.data.type;
          const categories = (type === "Raw Material Product" || type === "Raw Material (Supplier)")
            ? ["Virgin Product", "Reused Product"]
            : (type && envFactorOptions?.[type] ? Object.keys(envFactorOptions[type]) : []);
          return { component: GridSelectEditor, params: makeEditorParams(categories)(params) };
        }
      },
      {
        headerName: "Sub Category", field: "sub_category", ...defaultColDef, ...leftAlignColDef, editable: editable, _group_id: "user", pinned: null,
        cellEditorSelector: (params) => {
          const { type, category } = params.data;
          const subcats = (type === "Raw Material Product" || type === "Raw Material (Supplier)")
            ? []
            : (type && category && envFactorOptions?.[type]?.[category || ""]
              ? Object.keys(envFactorOptions[type][category || ""])
              : []);
          return { component: GridSelectEditor, params: makeEditorParams(subcats)(params) };
        }
      },
      {
        headerName: "Under Category", field: "description", ...defaultColDef, ...leftAlignColDef, pinned: activeView === "reference" ? "left" : null,
        filter: true,
        suppressHeaderMenuButton: false,
        editable: (params) => {
          // Allow editing if the row was manually added or is being overridden in user entry
          return editable && activeView === "user_entry";
        },
        cellEditorSelector: (params) => {
          if (params.data.type === "User Entry") {
            return { component: "agTextCellEditor" };
          }
          const { type, category, sub_category, description } = params.data;
          let descriptions = [];
          if (type === "Raw Material Product" || type === "Raw Material (Supplier)") {
            descriptions = supplierData.map((item) => (item.supplier ? `${item.supplier}_${item.name}` : item.name));
            descriptions.sort();
          } else {
            descriptions = type && category && envFactorOptions?.[type]?.[category || ""]?.[sub_category || ""]
              ? Object.keys(envFactorOptions[type][category || ""][sub_category || ""])
              : [];
          }
          // ✅ KEY FIX: Inject current row's description at the top if it's not already in the DB options
          // This shows the BOM-uploaded name (e.g. "x") alongside DB options (a, b, c) → user sees [x, a, b, c]
          if (description && !descriptions.includes(description)) {
            descriptions = [description, ...descriptions];
          }
          return { component: GridSelectEditor, params: makeEditorParams(descriptions)(params) };
        },
        cellStyle: (params) => activeView === "reference" ? getRefStyle(leftAlignColDef.cellStyle)(params) : leftAlignColDef.cellStyle,
        minWidth: 250
      },
      {
        headerName: "Description",
        field: "user_description",
        ...defaultColDef,
        ...leftAlignColDef,
        editable: editable,
        minWidth: 200,
        tooltipValueGetter: (params) => params.value || null,
        _group_id: "user"
      },

      // ════ REFERENCE DATA GROUP (Factor precision: 4 digits) ════
      {
        headerName: "GWP per Ref. Unit",
        field: "gwp_unit",
        ...defaultColDef,
        ...rightAlignColDef,
        ...floatFormater,
        editable: (params) => editable && (params.data?.type === "User Entry" || params.data?.type === "User Defined" || params.data?.source_type === "user_defined"),
        cellEditor: "agTextCellEditor",
        cellStyle: (params) => {
          const isUserEntry = params.data?.type === "User Entry" || params.data?.type === "User Defined" || params.data?.source_type === "user_defined";
          if (isUserEntry && editable) {
            return rightAlignColDef.cellStyle;
          }
          return getRefStyle(rightAlignColDef.cellStyle)(params);
        },
        _group_id: "ref"
      },
      {
        headerName: activeView === "reference" ? "Biogenic GWP / Ref. unit (kg CO2e)" : "Biogenic GWP (kg CO₂e)",
        field: "biogenic_gwp",
        ...defaultColDef,
        ...rightAlignColDef,
        ...floatFormater,
        editable: (params) => editable && (params.data?.type === "User Entry" || params.data?.type === "User Defined" || params.data?.source_type === "user_defined"),
        cellEditor: "agTextCellEditor",
        cellStyle: (params) => {
          const isUserEntry = params.data?.type === "User Entry" || params.data?.type === "User Defined" || params.data?.source_type === "user_defined";
          if (isUserEntry && editable) {
            return rightAlignColDef.cellStyle;
          }
          if (activeView === "reference") {
            return getRefStyle(rightAlignColDef.cellStyle)(params);
          }
          return getDerivedStyle(rightAlignColDef.cellStyle)(params);
        },
        _group_id: "ref"
      },
      { headerName: "Reference Unit", field: "default_unit", ...defaultColDef, ...leftAlignColDef, editable: false, cellStyle: getRefStyle(leftAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Factor (Ref.)", field: "factor", ...defaultColDef, ...rightAlignColDef, ...float4Formater, editable: false, cellStyle: getRefStyle(rightAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Unit Conv. Factor", field: "unit_conversion_factor", ...defaultColDef, ...rightAlignColDef, ...float4Formater, editable: false, cellStyle: getRefStyle(rightAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Ref. Length", field: "length", ...defaultColDef, ...rightAlignColDef, ...nullableFloatFormater, editable: false, cellStyle: getRefStyle(rightAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Ref. Length Unit", field: "length_unit", ...defaultColDef, ...leftAlignColDef, editable: false, cellStyle: getRefStyle(leftAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Ref. Width", field: "width", ...defaultColDef, ...rightAlignColDef, ...nullableFloatFormater, editable: false, cellStyle: getRefStyle(rightAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Ref. Width Unit", field: "width_unit", ...defaultColDef, ...leftAlignColDef, editable: false, cellStyle: getRefStyle(leftAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Ref. Thickness", field: "thickness", ...defaultColDef, ...rightAlignColDef, ...nullableFloatFormater, editable: false, cellStyle: getRefStyle(rightAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Ref. Thickness Unit", field: "thickness_unit", ...defaultColDef, ...leftAlignColDef, editable: false, cellStyle: getRefStyle(leftAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Volume (Ref.)", field: "volume", ...defaultColDef, ...rightAlignColDef, ...nullableFloatFormater, valueGetter: getVolumeInTargetUnit, editable: false, cellStyle: getDerivedStyle(rightAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Ref Density", field: "density", ...defaultColDef, ...rightAlignColDef, ...floatFormater, editable: false, cellStyle: getRefStyle(rightAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Ref Density for Unit Dimensions", field: "ref_unit", ...defaultColDef, ...leftAlignColDef, editable: false, cellStyle: getRefStyle(leftAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Density (kg/m³)", field: "density_kg_m3", ...defaultColDef, ...rightAlignColDef, ...nullableFloatFormater, editable: false, cellStyle: getRefStyle(rightAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Ref. Mass (kg)", field: "ref_mass_kg", ...defaultColDef, ...rightAlignColDef, ...nullableFloatFormater, editable: false, cellStyle: getRefStyle(rightAlignColDef.cellStyle), _group_id: "ref" },
      { headerName: "Uncertainty (Ref.)", field: "uncertainty_factor", ...defaultColDef, ...rightAlignColDef, ...nullableFloatFormater, editable: false, cellStyle: getRefStyle(rightAlignColDef.cellStyle), _group_id: "ref" },

      // ════ USER ENTRY GROUP (Primary Workspace) ════
      { headerName: "Quantity", field: "quantity", ...floatFormater, ...rightAlignColDef, ...defaultColDef, editable: editable, _group_id: "user" },
      {
        headerName: "Unit", field: "unit", ...rightAlignColDef, ...defaultColDef,
        editable: (params) => ((params.data.type === "Raw Material Product" || params.data.type === "Raw Material (Supplier)") ? false : editable),
        cellEditorSelector: (params) => {
          const { type, category, sub_category, description, unit: currentUnit, default_unit: rowDefaultUnit } = params.data;
          let units = [];
          if (type === "User Entry") {
            units = ["kg", "ton", "tonnes", "g", "lb", "m", "m2", "m3", "unit", "piece", "pcs"];
          } else {
            // 1. Resolve base unit from row data first (default_unit or current unit)
            let baseUnit = rowDefaultUnit || currentUnit;

            // 2. Fall back to reference options if row units are missing
            if (!baseUnit) {
              baseUnit = envFactorOptions?.[type]?.[category || ""]?.[sub_category || ""]?.[description]?.[0]?.unit;
            }

            // 3. Fall back to search in options across categories if exact path doesn't match
            if (!baseUnit && description && envFactorOptions) {
              for (const t of Object.values(envFactorOptions)) {
                for (const c of Object.values(t || {})) {
                  for (const s of Object.values(c || {})) {
                    if (s?.[description]?.[0]?.unit) {
                      baseUnit = s[description][0].unit;
                      break;
                    }
                  }
                  if (baseUnit) break;
                }
                if (baseUnit) break;
              }
            }

            const matchedUnits = new Set();
            if (baseUnit) {
              GetUnits(baseUnit).forEach((u) => matchedUnits.add(u));
            }
            if (rowDefaultUnit && rowDefaultUnit !== baseUnit) {
              GetUnits(rowDefaultUnit).forEach((u) => matchedUnits.add(u));
            }
            if (currentUnit && currentUnit !== baseUnit) {
              GetUnits(currentUnit).forEach((u) => matchedUnits.add(u));
            }

            // Always ensure the row's current unit and default unit are present in the list
            if (currentUnit) matchedUnits.add(currentUnit);
            if (rowDefaultUnit) matchedUnits.add(rowDefaultUnit);

            // Also allow discrete units (unit, piece) if dimensions/volume/area/distance/weight are supported
            const cat = GetUnitCategory(baseUnit || currentUnit || rowDefaultUnit);
            if (["volume", "area", "distance", "weight"].includes(cat)) {
              matchedUnits.add("unit");
              matchedUnits.add("piece");
            }

            // If still empty (unknown unit type), provide all common units
            if (matchedUnits.size === 0) {
              ["kg", "ton", "tonnes", "g", "lb", "m", "m2", "m3", "unit", "piece", "pcs"].forEach((u) => matchedUnits.add(u));
            }

            units = Array.from(matchedUnits);
          }
          const onValueChanged = (value) => {
            const newParams = { ...params, data: { ...params.data, [params.colDef.field]: value }, newValue: value, oldValue: params.value };
            onCellValueChanged(newParams);
          };
          return { component: GridSelectEditor, params: { values: units, onCellValueChanged: onValueChanged } };
        },
        _group_id: "user",
      },
      { headerName: "GWP (kg CO₂e)", field: "gwp_total", ...floatFormater, ...rightAlignColDef, ...defaultColDef, editable: (params) => editable && params.data.type === "User Entry", cellStyle: getDerivedStyle(rightAlignColDef.cellStyle), _group_id: "user" },
      { headerName: "Biogenic GWP Total", field: "biogenic_gwp_total", ...floatFormater, ...rightAlignColDef, ...defaultColDef, editable: (params) => editable && params.data.type === "User Entry", cellStyle: getDerivedStyle(rightAlignColDef.cellStyle), _group_id: "user" },
      {
        headerName: "Mass (kg)",
        field: "mass",
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || isNaN(params.value)) {
            return "NA";
          }
          return Number(params.value).toLocaleString("en-CA", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
        },
        tooltipValueGetter: (params) => {
          if (params.value === null || params.value === undefined || isNaN(params.value)) {
            const l = params.data.user_length ?? params.data.length;
            const w = params.data.user_width ?? params.data.width;
            const t = params.data.user_thickness ?? params.data.thickness;
            const hasDims = l && w && t;
            if (hasDims) {
              return "Density is missing in reference data. Please select a material with a valid density to calculate physical mass.";
            }
            return "Please enter dimensions (length, width, thickness) to calculate physical mass.";
          }
          return null;
        },
        ...rightAlignColDef,
        ...defaultColDef,
        editable: (params) => editable && params.data.type === "User Entry",
        cellStyle: getDerivedStyle(rightAlignColDef.cellStyle),
        _group_id: "user"
      },
      { headerName: "Uncertainty", field: "uncertainty", ...floatFormater, ...rightAlignColDef, ...defaultColDef, editable: editable, _group_id: "user" },
      { headerName: "User Factor", field: "user_factor", ...floatFormater, ...rightAlignColDef, ...defaultColDef, editable: editable, _group_id: "user" },

      { headerName: "Length", field: "user_length", ...defaultColDef, ...nullableFloatFormater, ...rightAlignColDef, editable: isUnitEditable, _group_id: "user" },
      {
        headerName: "Length Unit", field: "user_length_unit", cellEditor: GridSelectEditor,
        cellEditorSelector: (params) => ({ component: GridSelectEditor, params: makeEditorParams(distanceUnits)(params) }),
        ...defaultColDef, ...leftAlignColDef, editable: isUnitEditable, _group_id: "user"
      },
      { headerName: "Width", field: "user_width", ...defaultColDef, ...nullableFloatFormater, ...rightAlignColDef, editable: isUnitEditable, _group_id: "user" },
      {
        headerName: "Width Unit", field: "user_width_unit", cellEditor: GridSelectEditor,
        cellEditorSelector: (params) => ({ component: GridSelectEditor, params: makeEditorParams(distanceUnits)(params) }),
        ...defaultColDef, ...leftAlignColDef, editable: isUnitEditable, _group_id: "user"
      },
      { headerName: "Thickness", field: "user_thickness", ...defaultColDef, ...nullableFloatFormater, ...rightAlignColDef, editable: isUnitEditable, _group_id: "user" },
      {
        headerName: "Thickness Unit", field: "user_thickness_unit", cellEditor: GridSelectEditor,
        cellEditorSelector: (params) => ({ component: GridSelectEditor, params: makeEditorParams(distanceUnits)(params) }),
        ...defaultColDef, ...leftAlignColDef, editable: isUnitEditable, _group_id: "user"
      },
      { headerName: "Volume", field: "user_volume", ...defaultColDef, ...nullableFloatFormater, ...rightAlignColDef, valueGetter: getUserVolumeInTargetUnit, editable: false, cellStyle: getDerivedStyle(rightAlignColDef.cellStyle), _group_id: "user" },
      {
        headerName: "Volume Unit", field: "user_volume_unit", cellEditor: GridSelectEditor,
        cellEditorSelector: (params) => ({ component: GridSelectEditor, params: makeEditorParams(volumeUnits)(params) }),
        ...defaultColDef, ...leftAlignColDef, editable: isUnitEditable, _group_id: "user"
      },

      { headerName: "Part Number", field: "part_number", ...defaultColDef, ...leftAlignColDef, editable: editable, _group_id: "user" },
      {
        headerName: "Confidence",
        field: "confidence_score",
        ...defaultColDef,
        ...centerAlignColDef,
        minWidth: 140,
        width: 140,
        _group_id: "user",
        cellRenderer: (params) => {
          const raw = params.value;
          const score = raw !== null && raw !== undefined ? parseFloat(raw) : 1.0;
          const pct = Math.round(score * 100);
          const isUnmapped = score === 0 || params.data?.data_source === "unmapped" || params.data?.mapping_status === "unmapped";

          let bg = isDark ? "rgba(34, 197, 94, 0.15)" : "#dcfce7";
          let color = isDark ? "#4ade80" : "#16a34a";
          let border = "none";
          let label = `${pct}%`;

          if (isUnmapped) {
            bg = isDark ? "rgba(239, 68, 68, 0.15)" : "#fee2e2";
            color = isDark ? "#f87171" : "#dc2626";
            border = isDark ? "1px solid rgba(239, 68, 68, 0.4)" : "1px solid rgba(220, 38, 38, 0.3)";
            label = "Unmapped (0%)";
          } else if (score >= 0.90) {
            bg = isDark ? "rgba(34, 197, 94, 0.15)" : "#dcfce7";
            color = isDark ? "#4ade80" : "#16a34a";
            label = `${pct}%`;
          } else if (score >= 0.75) {
            bg = isDark ? "rgba(245, 158, 11, 0.15)" : "#fef3c7";
            color = isDark ? "#fbbf24" : "#d97706";
            label = `${pct}%`;
          } else if (score < 0.75) {
            bg = isDark ? "rgba(239, 68, 68, 0.15)" : "#fee2e2";
            color = isDark ? "#f87171" : "#dc2626";
            label = `${pct}%`;
          }

          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "2px 8px",
                borderRadius: "10px",
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: bg,
                color: color,
                border: border,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {label}
            </span>
          );
        },
      },
      {
        headerName: "Hotspot", field: "is_hotspot", ...defaultColDef, ...leftAlignColDef, _group_id: "user", cellStyle: getDerivedStyle(leftAlignColDef.cellStyle),
        cellRenderer: (params) => {
          const v = params.value;
          const isHotspot = v === 1 || v === true || String(v).toLowerCase() === "true";
          if (isHotspot) {
            return (
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "2px 8px",
                borderRadius: "12px",
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: "rgba(239, 68, 68, 0.12)",
                color: "#ef4444",
                border: "1px solid rgba(239, 68, 68, 0.2)"
              }}>
                🔥 Hotspot
              </span>
            );
          }
          return (
            <span style={{
              fontSize: "12px",
              color: isDark ? "#94a3b8" : "#64748b",
            }}>
              Normal
            </span>
          );
        },
      },

      // ════ TRAILING COLUMNS ════
      {
        headerName: "Data Source",
        field: "gwp_reference",
        ...defaultColDef,
        ...leftAlignColDef,
        editable: (params) => editable && params.data.type === "User Entry",
        cellStyle: (params) => activeView === "reference" ? getRefStyle(leftAlignColDef.cellStyle)(params) : leftAlignColDef.cellStyle,
        _group_id: "ref",
        minWidth: 350,
        width: 350,
        cellRenderer: (params) => {
          const val = params.value;
          if (val && (val.startsWith("http://") || val.startsWith("https://"))) {
            return (
              <a
                href={val}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#2563eb", textDecoration: "underline", cursor: "pointer" }}
                onClick={(e) => e.stopPropagation()}
              >
                EPD Library
              </a>
            );
          }
          return val || "";
        }
      },
      { headerName: "Note", field: "note", ...defaultColDef, ...leftAlignColDef, editable: editable, minWidth: 200 },
      {
        headerName: "Supplier", field: "supplier_name", ...defaultColDef, ...leftAlignColDef, editable: editable,
        _group_id: "user"
      },
      { headerName: "Year", field: "year", ...defaultColDef, ...leftAlignColDef, editable: false, cellStyle: (params) => activeView === "reference" ? getRefStyle(leftAlignColDef.cellStyle)(params) : leftAlignColDef.cellStyle, _group_id: "ref" },
      { headerName: "Geography", field: "geography", ...defaultColDef, ...leftAlignColDef, editable: false, cellStyle: (params) => activeView === "reference" ? getRefStyle(leftAlignColDef.cellStyle)(params) : leftAlignColDef.cellStyle, _group_id: "ref" },
      { headerName: "LCIA Method", field: "lcia_method", ...defaultColDef, ...leftAlignColDef, editable: false, cellStyle: (params) => activeView === "reference" ? getRefStyle(leftAlignColDef.cellStyle)(params) : leftAlignColDef.cellStyle, _group_id: "ref" },
      { headerName: "Recyclability", field: "recyclability", ...defaultColDef, ...rightAlignColDef, ...nullableFloatFormater, editable: false, cellStyle: (params) => activeView === "reference" ? getRefStyle(rightAlignColDef.cellStyle)(params) : rightAlignColDef.cellStyle, _group_id: "ref" },

    ];

    const visibleFields = getVisibleFieldsForView(activeView);
    if (visibleFields) {
      const fieldOrderMap = new Map(visibleFields.map((f, i) => [f, i]));
      baseCols.sort((a, b) => {
        const orderA = fieldOrderMap.has(a.field) ? fieldOrderMap.get(a.field) : 9999;
        const orderB = fieldOrderMap.has(b.field) ? fieldOrderMap.get(b.field) : 9999;
        return orderA - orderB;
      });
    }

    return baseCols.map((c) => ({ ...c, hide: isHidden(c.field) }));
  }, [columnVisibilities, editable, supplierData, envFactorOptions, supplier, product, colVisibility_key, isSupplier, projectData, getVolume, getUserVolume, activeView, isDark, lcaStage]);

  const defaultCols = colDefs.map((col) => col.field);

  const totalGWP = useMemo(() => {
    const dataToSum = selectedEnvFactorIds.length > 0 ? localEnvFactors.filter((ef) => selectedEnvFactorIds.includes(ef.id)) : localEnvFactors;

    return formatTo2Decimals(
      dataToSum.reduce((total, row) => {
        const isStageD = row.lca_stage && row.lca_stage.trim().startsWith("D");
        if (isStageD) return total;

        const gwpTotal = row.gwp_total && !isNaN(row.gwp_total) ? parseFloat(row.gwp_total) : 0;
        if (gwpTotal < 0) return total; // legacy purely biogenic subtraction rows

        return total + gwpTotal;
      }, 0)
    );
  }, [localEnvFactors, selectedEnvFactorIds]);
  const totalBiogenicGWP = useMemo(() => {
    const dataToSum = selectedEnvFactorIds.length > 0 ? localEnvFactors.filter((ef) => selectedEnvFactorIds.includes(ef.id)) : localEnvFactors;
    return formatTo2Decimals(
      dataToSum.reduce((total, row) => {
        const isStageD = row.lca_stage && row.lca_stage.trim().startsWith("D");
        if (isStageD) return total;

        const gwpTotal = row.gwp_total && !isNaN(row.gwp_total) ? parseFloat(row.gwp_total) : 0;
        const biogenicGWP = row.biogenic_gwp_total && !isNaN(row.biogenic_gwp_total) ? parseFloat(row.biogenic_gwp_total) : 0;

        const bioContribution = biogenicGWP !== 0 ? biogenicGWP : (gwpTotal < 0 ? gwpTotal : 0);

        return total + bioContribution;
      }, 0)
    );
  }, [localEnvFactors, selectedEnvFactorIds]);

  const totalMass = useMemo(() => {
    const dataToSum = selectedEnvFactorIds.length > 0 ? localEnvFactors.filter((ef) => selectedEnvFactorIds.includes(ef.id)) : localEnvFactors;

    const sum = dataToSum.reduce((total, row) => {
      const isStageD = row.lca_stage && row.lca_stage.trim().startsWith("D");
      if (isStageD) return total;

      const mass = row.mass && !isNaN(row.mass) ? parseFloat(row.mass) : 0;
      return total + mass;
    }, 0);

    return formatTo2Decimals(sum);
  }, [localEnvFactors, selectedEnvFactorIds]);

  const getRowStyle = useCallback((params) => {
    if (params.data) {
      // Task 3: Selected row — Arivu green highlight from org palette
      const isSelected = params.node?.isSelected?.();
      if (isSelected) {
        const green = tok.bg.tabActive || "#659d33";
        return {
          backgroundColor: isDark ? alpha(green, 0.22) : alpha(green, 0.16),
          borderLeft: `3px solid ${green}`,
        };
      }

      // Unmapped row highlight (amber/warning border & tint for items requiring review)
      const isUnmapped = params.data.data_source === "unmapped" || 
                         params.data.mapping_status === "unmapped" || 
                         parseFloat(params.data.confidence_score) === 0 ||
                         (params.data.gwp_unit !== null && parseFloat(params.data.gwp_unit) === 0 && params.data.type !== "Wood");
      if (isUnmapped) {
        return {
          backgroundColor: isDark ? "rgba(245, 158, 11, 0.08)" : "rgba(254, 243, 199, 0.35)",
          borderLeft: "3px solid #f59e0b",
        };
      }

      // Zero-quantity rows: subtle green tint
      const q = params.data.quantity;
      if (q === 0 || q === "0" || parseFloat(q) === 0) {
        return {
          backgroundColor: isDark ? alpha(tok.bg.tabActive || "#659d33", 0.08) : alpha(tok.bg.tabActive || "#659d33", 0.06),
        };
      }
    }
    return null;
  }, [isDark, tok]);

  const footerRight = (
    <Box sx={{ display: "flex", gap: 3, alignItems: "center", pr: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#a1a1aa" : "#64748b" }}>
        Total Mass (kg): <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: isDark ? "#ffffff" : "#0f172a" }}>{totalMass}</Typography>
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#a1a1aa" : "#64748b" }}>
        Total Biogenic: <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: isDark ? "#ffffff" : "#0f172a" }}>{totalBiogenicGWP}</Typography>
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#a1a1aa" : "#64748b" }}>
        Total Fossil: <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: isDark ? "#ffffff" : "#0f172a" }}>{totalGWP}</Typography>
      </Typography>
    </Box>
  );

  return (
    <Box>
      <TableHeaderContainer ref={gridHeaderContainerRef}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
          <TableTitleContainer>
            <Typography variant="Table Title">{t("Environmental Factors")}</Typography>
          </TableTitleContainer>

          {/* High-End Embedded Switcher */}
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: tok.bg.tabContainer,
            p: "3px",
            borderRadius: tok.radius.tab,
            border: `1px solid ${tok.border.divider}`,
            boxShadow: "0 1px 2px rgba(0,0,0,0.02)"
          }}>
            {ENV_FACTOR_VIEW_MODES.map((mode) => {
              const Icon = mode.icon;
              const isSelected = activeView === mode.id;
              return (
                <Button
                  key={mode.id}
                  variant="text"
                  onClick={() => setActiveView(mode.id)}
                  startIcon={<Icon size={15} />}
                  title={mode.desc}
                  sx={{
                    px: 1.5,
                    py: "5px",
                    minWidth: "unset",
                    borderRadius: tok.radius.tabItem,
                    textTransform: "none",
                    fontSize: tok.font.size.tabLabel,
                    fontWeight: isSelected ? tok.font.weight.semibold : tok.font.weight.medium,
                    fontFamily: tok.font.family,
                    color: isSelected ? tok.text.tabActive : tok.text.tabInactive,
                    bgcolor: isSelected ? tok.bg.tabActive : "transparent",
                    boxShadow: isSelected ? tok.shadow.tabActive : "none",
                    "&:hover": {
                      bgcolor: isSelected ? tok.bg.tabActive : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
                      opacity: 0.9
                    },
                    transition: "all 0.15s ease-in-out"
                  }}
                >
                  {mode.label}
                </Button>
              );
            })}
          </Box>
        </Box>

        <TableHeaderToolsContainer>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LuDatabase size={15} />}
            onClick={() => {
              setEpdDialogMode("add");
              setEpdInitialSearch("");
              setEpdDialogOpen(true);
            }}
            sx={{
              height: "36px",
              px: 1.75,
              mr: 1.5,
              textTransform: "none",
              fontFamily: tok.font.family,
              fontSize: "13px",
              fontWeight: 600,
              borderRadius: tok.radius.input || "8px",
              borderColor: isDark ? "rgba(101, 157, 51, 0.4)" : "rgba(101, 157, 51, 0.5)",
              color: isDark ? "#ffffff" : tok.bg.tabActive || "#659d33",
              bgcolor: isDark ? "rgba(101, 157, 51, 0.12)" : "rgba(101, 157, 51, 0.06)",
              "&:hover": {
                bgcolor: isDark ? "rgba(101, 157, 51, 0.22)" : "rgba(101, 157, 51, 0.12)",
                borderColor: tok.bg.tabActive || "#659d33",
              },
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            Library
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", marginRight: 2 }}>
            <MaterialSearchInput onSelect={onSearchSelect} />
          </Box>
          <input type="file" style={{ display: "none" }} ref={fileInputRef} onChange={handleFileUpload} accept=".pdf,.csv,.xlsx,.xls" />
          <UploadBOMButton onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={!processID} />
          <AddButton
            onClick={() => {
              const maxSourceRow = localEnvFactors.reduce((max, row) => {
                const val = parseInt(row.source_row, 10);
                return !isNaN(val) && val > max ? val : max;
              }, 0);
              setLocalEnvFactors((prev) => [...prev, { ...defaultEnvFactor, id: uuidv4(), source_row: maxSourceRow + 1 }]);
              setIsSaveVisible(true);
            }}
            disabled={!processID}
          />
          <RemoveButton
            onClick={() => {
              setLocalEnvFactors((prev) => {
                const remaining = prev.filter((a) => !selectedEnvFactorIds.includes(a.id));
                return remaining.map((row, index) => {
                  const newSeq = index + 1;
                  // Only mark modified if the sequence actually changed
                  if (row.source_row !== newSeq) {
                    return { ...row, source_row: newSeq, _isModified: true };
                  }
                  return row;
                });
              });
              setEnvFactorIdsToDelete((prev) => [...prev, ...selectedEnvFactorIds]);
              setIsSaveVisible(true);
            }}
            disabled={selectedEnvFactorIds.length < 1}
          />
          <UndoButton
            onClick={() => {
              const remaining = undo();
              setIsSaveVisible(remaining > 0);
            }}
            disabled={!canUndo}
          />
          <ExportAsXLSXButton
            data={localEnvFactors}
            filename={exportFilename}
            columnMapping={{
              source_row: "Sl No.",
              type: "Type",
              category: "Category",
              sub_category: "Sub Category",
              description: "Under Category",
              
              // GROUP A: REFERENCE DB COLUMNS (Original Baseline)
              gwp_unit: "GWP per Ref. Unit",
              biogenic_gwp: "Biogenic GWP / Ref. unit",
              default_unit: "Reference Unit",
              factor: "Factor (Ref.)",
              unit_conversion_factor: "Unit Conv. Factor",
              length: "Ref. Length",
              length_unit: "Ref. Length Unit",
              width: "Ref. Width",
              width_unit: "Ref. Width Unit",
              thickness: "Ref. Thickness",
              thickness_unit: "Ref. Thickness Unit",
              volume: "Volume (Ref.)",
              density: "Ref Density",
              ref_unit: "Ref Density for Unit Dimensions",
              density_kg_m3: "Density (kg/m³)",
              ref_mass_kg: "Ref. Mass (kg)",
              uncertainty_factor: "Uncertainty (Ref.)",
              year: "Year",
              geography: "Geography",
              lcia_method: "LCIA Method",
              recyclability: "Recyclability",
              
              // GROUP B: USER ENTRY COLUMNS (Overrides & Calculations)
              quantity: "Quantity",
              unit: "Unit",
              gwp_total: "GWP (kg CO2e)",
              biogenic_gwp_total: "Biogenic GWP Total (kg CO2e)",
              mass: "Mass (kg)",
              uncertainty: "Uncertainty",
              user_factor: "User Factor",
              user_length: "Length",
              user_length_unit: "Length Unit",
              user_width: "Width",
              user_width_unit: "Width Unit",
              user_thickness: "Thickness",
              user_thickness_unit: "Thickness Unit",
              user_volume: "Volume",
              user_volume_unit: "Volume Unit",
              part_number: "Part Number",
              gwp_reference: "Data Source",
              note: "Note",
              user_description: "Description",
              supplier_name: "Supplier",
              is_hotspot: "Hotspot"
            }}
          />
          {/* <LoadEnvFactorButton onClick={() => setEnvfactorMenuVisible((p) => !p)} /> */}
          <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
        </TableHeaderToolsContainer>
      </TableHeaderContainer>

      <AgGrid
        ref={gridRef}
        rowData={localEnvFactors}
        columnDefs={colDefs}
        onSelectionChanged={onSelectionChanged}
        onCellValueChanged={onCellValueChanged}
        headerCheckbox={false}
        components={{ MaterialSearchCellEditor }}
        colVisibility_key={colVisibility_key}
        footerRightContent={footerRight}
        maxHeight={maxHeight}
        singleClickEdit={true}
        handleContextMenu={handleContextMenu}
        pageSizeOptions={[5, 10, 25, 50, 100, "All"]}
        getRowStyle={getRowStyle}
        sx={{
          "& .ag-body-viewport": {
            direction: "ltr !important",
          },
          "& .ag-body-viewport *": {
            direction: "ltr !important",
          },
          "& .ag-row-selected": {
            backgroundColor: `${isDark ? alpha(tok.bg.tabActive || "#659d33", 0.22) : alpha(tok.bg.tabActive || "#659d33", 0.16)} !important`,
            borderLeft: `3px solid ${tok.bg.tabActive || "#659d33"} !important`,
          },
          "& .ag-row-selected .ag-cell": {
            backgroundColor: `${isDark ? alpha(tok.bg.tabActive || "#659d33", 0.22) : alpha(tok.bg.tabActive || "#659d33", 0.16)} !important`,
          },
          "& .ag-row-selected .non-editable-cell": {
            backgroundColor: `${isDark ? alpha(tok.bg.tabActive || "#659d33", 0.18) : alpha(tok.bg.tabActive || "#659d33", 0.12)} !important`,
          }
        }}
      />
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
        gridRef={gridRef}
        activeView={activeView}
      />
      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={selectedEnvFactorsLocal} options={contextMenuOptions} />

      {/* Geography Context Dialog */}
      <Dialog open={geoDialogOpen} onClose={() => { setGeoDialogOpen(false); setPendingFile(null); }}>
        <DialogTitle sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#0f172a" }}>
          Manufacturing Region
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 3, mt: 1 }}>
            Where were these materials primarily manufactured? The AI will use this context to accurately estimate the local carbon grid intensity for unknown materials.
          </Typography>
          <Select
            value={selectedGeography}
            onChange={(e) => setSelectedGeography(e.target.value)}
            fullWidth
            size="small"
            sx={{
              bgcolor: isDark ? "#1e293b" : "#ffffff",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? "#334155" : "#cbd5e1" },
            }}
          >
            <MenuItem value="Global Average">Global Average</MenuItem>
            <MenuItem value="North America (Canada/USA)">North America (Canada/USA)</MenuItem>
            <MenuItem value="Europe">Europe</MenuItem>
            <MenuItem value="Asia">Asia</MenuItem>
            <MenuItem value="India">India</MenuItem>
            <MenuItem value="China">China</MenuItem>
            <MenuItem value="Other">Other (Specify country/region)</MenuItem>
          </Select>
          {selectedGeography === "Other" && (
            <TextField
              placeholder="e.g., Vietnam, Germany, Brazil"
              value={customGeography}
              onChange={(e) => setCustomGeography(e.target.value)}
              fullWidth
              size="small"
              sx={{
                mt: 2,
                bgcolor: isDark ? "#1e293b" : "#ffffff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? "#334155" : "#cbd5e1" },
                input: { color: isDark ? "#ffffff" : "#0f172a" },
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => { setGeoDialogOpen(false); setPendingFile(null); }} sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmUpload} sx={{ bgcolor: tok.buttonPrimary, "&:hover": { bgcolor: tok.buttonPrimaryHover } }}>
            Continue Upload
          </Button>
        </DialogActions>
      </Dialog>
      <LoadEnvfactorMenu
        anchorRef={gridHeaderContainerRef}
        visible={envfactorMenuVisible}
        onClose={() => setEnvfactorMenuVisible(false)}
        onLoad={onLoadEnvFactors}
        onUpload={onUploadEnvFactors}
      />

      <EPDLibraryDialog
        open={epdDialogOpen}
        onClose={() => setEpdDialogOpen(false)}
        onSelect={handleEPDSelect}
        supplierData={supplierData}
        mode={epdDialogMode}
        initialSearch={epdInitialSearch}
        processID={processID}
        lcaStage={lcaStage}
        currentProductId={productID}
        currentProjectId={projectID}
      />

      {/* BOM Upload Status Overlay (Premium Glassmorphic AI Progress Terminal) */}
      <Dialog
        open={!!uploadStatus}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: 4,
            minWidth: 480,
            textAlign: 'center',
            backgroundColor: isDark ? 'rgba(15, 15, 25, 0.93)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(25px)',
            border: `1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(101, 157, 51, 0.15)'}`,
            boxShadow: isDark
              ? '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 50px rgba(101, 157, 51, 0.2), inset 0 1px 0 rgba(255,255,255,0.1)'
              : '0 25px 50px -12px rgba(101, 157, 51, 0.15), 0 0 50px rgba(101, 157, 51, 0.05), inset 0 1px 0 rgba(255,255,255,0.6)'
          }
        }}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3.5, p: 0.5, overflow: 'visible' }}>
          {/* Main Glowing Visual with Arivu Bot Logo & Orbiting Sparkles */}
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5, width: 110, height: 110 }}>
            {/* Pulsing Outer Neon Halo */}
            <Box
              sx={{
                position: 'absolute',
                width: 90,
                height: 90,
                borderRadius: '50%',
                backgroundColor: alpha(tok.bg.tabActive || '#659d33', 0.18),
                filter: 'blur(20px)',
                animation: 'halo-glow 3s infinite ease-in-out',
                '@keyframes halo-glow': {
                  '0%, 100%': { transform: 'scale(0.85)', opacity: 0.4 },
                  '50%': { transform: 'scale(1.25)', opacity: 0.9 }
                }
              }}
            />
            {/* Spinning Neon Ring */}
            <Box
              sx={{
                position: 'absolute',
                width: 78,
                height: 78,
                borderRadius: '50%',
                border: `2px dashed ${tok.bg.tabActive || '#659d33'}`,
                animation: 'spin-ring 10s infinite linear',
                '@keyframes spin-ring': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
            {/* Core Logo Avatar with custom frame */}
            <Avatar
              src={arivufoot}
              alt="Arivu AI"
              sx={{
                position: 'relative',
                width: 62,
                height: 62,
                bgcolor: isDark ? '#14141d' : '#f8fafc',
                border: `2.5px solid ${tok.bg.tabActive || '#659d33'}`,
                zIndex: 2,
                boxShadow: isDark
                  ? '0 10px 25px rgba(0, 0, 0, 0.5), 0 0 20px rgba(101, 157, 51, 0.3)'
                  : '0 10px 25px rgba(101, 157, 51, 0.2)',
                '& img': { objectFit: 'contain', padding: '10px' }
              }}
            />
            {/* Orbital Floating Sparkles */}
            <Box sx={{ position: 'absolute', top: 4, right: 4, animation: 'orbit-cw 3.5s infinite linear', zIndex: 3, '@keyframes orbit-cw': { '0%': { transform: 'rotate(0deg) translate(40px) rotate(0deg)' }, '100%': { transform: 'rotate(360deg) translate(40px) rotate(-360deg)' } } }}>
              <LuSparkles size={14} style={{ color: tok.bg.tabActive || '#659d33', filter: 'drop-shadow(0 0 5px rgba(101,157,51,0.8))' }} />
            </Box>
            <Box sx={{ position: 'absolute', bottom: 4, left: 4, animation: 'orbit-ccw 4.5s infinite linear', zIndex: 3, '@keyframes orbit-ccw': { '0%': { transform: 'rotate(360deg) translate(44px) rotate(-360deg)' }, '100%': { transform: 'rotate(0deg) translate(44px) rotate(0deg)' } } }}>
              <LuSparkles size={11} style={{ color: isDark ? '#ffffff' : tok.bg.tabActive || '#659d33', opacity: 0.8 }} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: isDark ? 'rgba(101, 157, 51, 0.12)' : 'rgba(101, 157, 51, 0.08)',
                border: `1px solid ${alpha(tok.bg.tabActive || '#659d33', 0.25)}`,
                borderRadius: '20px',
                px: 2,
                py: 0.5,
                mb: 0.5
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: '#10b981',
                  animation: 'pulse-dot 1.2s infinite ease-in-out',
                  '@keyframes pulse-dot': { '0%, 100%': { transform: 'scale(0.8)', opacity: 0.5 }, '50%': { transform: 'scale(1.3)', opacity: 1 } }
                }}
              />
              <Typography sx={{ fontSize: '10px', fontWeight: 800, color: tok.bg.tabActive || '#659d33', letterSpacing: '1px', fontFamily: tok.font.family, textTransform: 'uppercase' }}>
                AI Core Processing
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '20px', fontWeight: 800, color: isDark ? '#ffffff' : '#0f172a', fontFamily: tok.font.family, letterSpacing: '-0.5px' }}>
              Arivu is thinking...
            </Typography>
          </Box>

          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}>
            <LinearProgress
              variant="determinate"
              value={uploadStatus ? Math.min(Math.round((uploadStatus.step / 7) * 100), 95) : 0}
              sx={{
                width: '100%',
                borderRadius: '8px',
                height: 8,
                backgroundColor: isDark ? '#1a1a24' : '#f1f5f9',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: tok.bg.tabActive || '#659d33',
                  borderRadius: '8px',
                  boxShadow: '0 0 12px rgba(101, 157, 51, 0.5)'
                }
              }}
            />
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: isDark ? '#71717a' : '#94a3b8', fontFamily: 'monospace' }}>
              {uploadStatus ? Math.min(Math.round((uploadStatus.step / 7) * 100), 95) : 0}% COMPLETE
            </Typography>
          </Box>

          {/* Interactive vertical LCA Step Timeline */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'left', px: 0.5 }}>
            {[
              "Parsing document structure...",
              "Initiating material library cross-reference...",
              "Resolving custom material descriptions...",
              "Sourcing global EPD datasets...",
              "Verifying physical conversion scales...",
              "Applying LCA conversion bounds...",
              "Saving materials to database..."
            ].map((stepName, idx) => {
              const stepNum = idx + 1;
              const isActive = uploadStatus?.step === stepNum;
              const isCompleted = uploadStatus?.step > stepNum;

              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: '8px 14px',
                    borderRadius: '10px',
                    backgroundColor: isActive
                      ? (isDark ? 'rgba(101, 157, 51, 0.08)' : 'rgba(101, 157, 51, 0.05)')
                      : 'transparent',
                    border: isActive
                      ? `1px solid ${alpha(tok.bg.tabActive || '#659d33', 0.25)}`
                      : '1px solid transparent',
                    transition: 'all 0.3s ease',
                    opacity: isCompleted || isActive ? 1 : 0.35,
                    transform: isActive ? 'scale(1.01)' : 'scale(1)'
                  }}
                >
                  {/* Status Indicator Icon */}
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isCompleted
                        ? (tok.bg.tabActive || '#659d33')
                        : 'transparent',
                      border: isCompleted
                        ? 'none'
                        : `2.5px solid ${isActive ? (tok.bg.tabActive || '#659d33') : (isDark ? '#3f3f46' : '#cbd5e1')}`,
                      transition: 'all 0.3s ease',
                      boxShadow: isCompleted ? '0 0 10px rgba(101, 157, 51, 0.3)' : 'none'
                    }}
                  >
                    {isCompleted ? (
                      <LuCircleCheck size={14} color="#fff" />
                    ) : (
                      isActive && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: tok.bg.tabActive || '#659d33',
                            animation: 'ping 1.2s infinite ease-in-out',
                            '@keyframes ping': {
                              '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                              '50%': { transform: 'scale(1.4)', opacity: 1 },
                              '100%': { transform: 'scale(0.8)', opacity: 0.5 }
                            }
                          }}
                        />
                      )
                    )}
                  </Box>
                  {/* Step Text */}
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: isActive ? 700 : 500,
                      color: isActive
                        ? (isDark ? '#ffffff' : '#0f172a')
                        : (isCompleted ? (isDark ? '#a1a1aa' : '#64748b') : (isDark ? '#52525b' : '#94a3b8')),
                      fontFamily: tok.font.family,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {stepName}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* High-End Terminal Console Log for LCA Subtasks */}
          <Box
            sx={{
              width: '100%',
              backgroundColor: '#07070a',
              border: '1.5px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '12px',
              p: 2,
              textAlign: 'left',
              fontFamily: 'monospace',
              fontSize: '11px',
              color: '#34d399',
              boxShadow: 'inset 0 3px 15px rgba(0,0,0,0.9)',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)', pb: 0.75, mb: 1, color: '#4b5563' }}>
              <span>arivu-ai-shell v1.0.4</span>
              <span style={{ color: '#10b981', fontWeight: 'bold' }}>● ACTIVE</span>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {(() => {
                const step = uploadStatus?.step || 1;
                const logs = [
                  [
                    "> [SYS] Initializing BOM upload stream...",
                    "> [SYS] Secure connection established with Arivu API server.",
                    "> [SYS] Ingesting document file bytes... OK"
                  ],
                  [
                    "> [PARSER] Scanning document structure via Arivu Engine...",
                    "> [PARSER] Matching table rows and header identifiers...",
                    "> [PARSER] Extracted raw items successfully."
                  ],
                  [
                    "> [LCA-DB] Initiating two-tier material cross-reference registry...",
                    "> [LCA-DB] Tier 1: Matching keywords in local cache...",
                    "> [LCA-DB] Tier 2: Resolving descriptions via Arivu Engine..."
                  ],
                  [
                    `> [GEO-RESOLVER] Location filter: ${selectedGeography === "Other" ? (customGeography || "Custom") : selectedGeography}...`,
                    "> [GEO-RESOLVER] Analyzing grid emission factors for referenced regions...",
                    "> [GEO-RESOLVER] Matching verified ecoinvent/EPD databases..."
                  ],
                  [
                    "> [CONVERTER] Calibrating physical dimensions (pieces, kg, tons)...",
                    "> [CONVERTER] Running density scale normalizations...",
                    "> [CONVERTER] Applying custom thickness/weight boundary rules."
                  ],
                  [
                    "> [ESTIMATOR] Ingesting GWP bounds for carbon calculations...",
                    "> [ESTIMATOR] Computing Fossil vs. Biogenic GWP distribution...",
                    "> [ESTIMATOR] Verification passes successfully."
                  ],
                  [
                    "> [DATABASE] Writing environmental factors to local workspace...",
                    "> [DATABASE] Indexing process IDs to MySQL Tables...",
                    "> [SYSTEM] Material pipeline ingestion completed successfully!"
                  ]
                ];
                const currentLogs = logs[Math.min(step - 1, 6)];
                return currentLogs.map((logLine, lIdx) => (
                  <Box key={lIdx} sx={{ opacity: lIdx === 2 ? 0.95 : 0.6, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ color: '#fff' }}>{logLine}</span>
                    {lIdx === 2 && (
                      <Box
                        component="span"
                        sx={{
                          width: '6px',
                          height: '11px',
                          backgroundColor: '#34d399',
                          animation: 'blink 1s infinite steps(2, start)',
                          '@keyframes blink': { '0%, 100%': { opacity: 0 }, '50%': { opacity: 1 } }
                        }}
                      />
                    )}
                  </Box>
                ));
              })()}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* BOM Review Modal */}
      <BOMReviewDialog
        open={bomDialogOpen}
        onClose={() => setBomDialogOpen(false)}
        parsedData={parsedBomData}
        onConfirm={handleBOMConfirm}
      />
    </Box>
  );
}

export default EnvironmentFactorGrid;
